/**
 * API Service for LifeScribe Admin Dashboard
 * Handles all HTTP requests with authentication
 */

import { AdminUser, AddUserRequest, AddUserResponse, LoginRequest, LoginResponse, AuditLogEntry } from '../types/auth';
import { DashboardOverview } from '../types/dashboard';

/** Pagination shape returned by some admin list endpoints */
type ApiPagination = { total?: number; limit?: number; offset?: number; hasMore?: boolean };

/**
 * Normalized fetch wrapper result (backend may attach metrics/charts at top level)
 */
type FetchWithAuthResult<T = unknown> = {
  data: T;
  success: boolean;
  message?: string;
  pagination?: ApiPagination;
  /** Some list endpoints (e.g. segment members) return `total` alongside `data` */
  total?: number;
  metrics?: Record<string, unknown> | null;
  charts?: unknown;
  partialData?: boolean;
  queryTime?: number;
  timestamp?: string;
};

// ── Onboarding questionnaire: age x motivation cross-tab ─────────────────────
export interface OnboardingBreakdownRow {
  age_range: string;
  motivation: string;
  count: number;
}

export interface OnboardingBreakdownResponse {
  from: string;
  to: string;
  breakdown: OnboardingBreakdownRow[];
  answered_total: number;
  members_total: number;
}

export interface MemberSignupDate {
  email: string;
  created_at: string | null;
  /** Earliest Singular event for this member — NOT an install date. */
  first_app_activity_at: string | null;
}

export interface MemberSignupDatesResponse {
  members: MemberSignupDate[];
}

// ── Free Tier Conversion Engine: nurture funnel overview ─────────────────────
export interface EmailFunnelStepRow {
  id: string;
  step_order: number;
  step_type: string;
  label: string | null;
  delay_hours: number | null;
  include_offer: boolean;
  template_key: string | null;
  template_id: string | null;
  subject_line: string | null;
  template_ready: boolean | null;
  /** Wait steps carry their delay here; offsets and silence bars derive from it. */
  delay_minutes?: number | null;
  /**
   * Honest copy readiness (migration 1112). A stub can no longer render as
   * "Ready" — Plan 15 §2.3.
   */
  copy_state?: 'ready' | 'stub' | 'held' | 'rewrite_pending' | null;
  /** Why a step is held — e.g. "AWAITING BRAND RULING — mortality-adjacent". */
  copy_state_reason?: string | null;
  /** Other funnels sending this same template row. Editing it changes them too. */
  shared_with?: string[] | null;
}
export interface EmailFunnelRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  trigger_event: string;
  description: string | null;
  phase_key: string | null;
  /** 'pre' (conversion, F-series) or 'post' (retention, P-series). */
  funnel_group?: 'pre' | 'post';
  /**
   * V4 architecture class (migration 1122). 'lifecycle' = one of the five
   * personas, mutually exclusive per Rule 1. 'diagnostic' = one of the eight
   * always-on routes, which take precedence per Rule 4. 'legacy' = pre-V4.
   */
  funnel_class?: 'lifecycle' | 'diagnostic' | 'legacy';
  /** Stretches over 14 days with no send — the artifact's red bars. */
  silence_gaps?: { from_day: number; to_day: number; days: number }[];
  /**
   * The audience mirroring this sequence's entry rule (migration 1102).
   * Null for event-driven sequences (deletion win-back, dunning, cart
   * recovery) — they have no standing population to list.
   */
  audience?: { id: string; slug: string; name: string; count: number } | null;
  attached_promo_code: string | null;
  eligible_count: number;
  step_count: number;
  steps: EmailFunnelStepRow[];
  active_enrollments: number;
  completed_count: number;
  total_enrolled: number;
  conversion_rate: number | null;
  template_health: {
    total: number; ready: number; stubs: number; missing_keys: string[];
    by_state?: { ready: number; stub: number; held: number; rewrite_pending: number; missing: number };
    held_keys?: string[];
  };
  created_at: string;
  updated_at: string;
}
export interface EmailFunnelsOverview {
  success: boolean;
  summary: Record<string, number>;
  flows: EmailFunnelRow[];
}

class ApiService {
  private baseUrl = '';

  constructor() {
    // Base URL is empty since we're serving from the same domain
    this.baseUrl = '';
  }

  /**
   * Generic fetch wrapper with authentication and error handling
   */
  private async fetchWithAuth<T = unknown>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<FetchWithAuthResult<T>> {
    try {
      const isFormData = options.body instanceof FormData;
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        credentials: 'include', // Include cookies for authentication
        headers: {
          // Skip Content-Type for FormData — browser sets multipart boundary automatically
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          ...options.headers,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          const errBody = await response.json().catch(() => ({}));
          // Don't redirect immediately - let the calling component handle auth errors
          // This prevents race conditions in components that make multiple concurrent API calls
          throw new Error(errBody?.message || 'Unauthorized');
        }

        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ API Error Response:`, errorData);
        const detail =
          errorData.message ||
          errorData.error ||
          (Array.isArray(errorData.errors) && errorData.errors[0]?.msg) ||
          `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(typeof detail === 'string' ? detail : `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = (await response.json()) as Record<string, unknown> & {
        success?: boolean;
        message?: string;
        data?: unknown;
        total?: number;
        pagination?: ApiPagination;
        metrics?: Record<string, unknown> | null;
        charts?: unknown;
        partialData?: boolean;
        queryTime?: number;
        timestamp?: string;
      };
      return {
        data: (data.data !== undefined ? data.data : data) as T,
        success: data.success !== false,
        message: data.message,
        metrics: data.metrics ?? null,
        charts: data.charts,
        partialData: data.partialData,
        queryTime: data.queryTime,
        timestamp: data.timestamp,
        pagination: data.pagination,
        total: typeof data.total === 'number' ? data.total : undefined,
      };
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Generic HTTP methods for direct API calls
   * V4.52: Added for email template/trigger components
   */
  async get<T = any>(endpoint: string): Promise<{ data: T; total?: number }> {
    const result = await this.fetchWithAuth<T>(endpoint, { method: 'GET' });
    return { data: result.data, ...(result.total !== undefined ? { total: result.total } : {}) };
  }

  async post<T = any>(
    endpoint: string,
    data?: any,
    options: RequestInit = {},
  ): Promise<{ data: T }> {
    const result = await this.fetchWithAuth<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data != null ? JSON.stringify(data) : undefined,
    });
    return { data: result.data };
  }

  async put<T = any>(endpoint: string, data?: any): Promise<{ data: T }> {
    const result = await this.fetchWithAuth<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
    return { data: result.data };
  }

  async delete<T = any>(endpoint: string): Promise<{ data: T }> {
    const result = await this.fetchWithAuth<T>(endpoint, { method: 'DELETE' });
    return { data: result.data };
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<{ data: T }> {
    const result = await this.fetchWithAuth<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
    return { data: result.data };
  }

  /**
   * Download file as blob (for PDFs, images, etc.)
   * V4.52: Added for PDF preview functionality
   */
  async downloadBlob(endpoint: string, method: 'GET' | 'POST' = 'POST', data?: any): Promise<Blob> {
    const options: RequestInit = {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (data && method === 'POST') {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, options);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Authentication APIs
   */
  async verifyAuth(): Promise<{ authenticated: boolean; user?: AdminUser }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/verify`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          const responseData = await response.json().catch(() => ({}));
          void responseData; // cookies_cleared flag consumed by server
          return { authenticated: false, user: undefined };
        }

        await response.json().catch(() => ({}));
        return { authenticated: false, user: undefined };
      }

      const data = await response.json();
      return { authenticated: data.authenticated, user: data.user };
    } catch (error) {
      console.error(`❌ [VERIFY_AUTH] Network or parsing error:`, error);
      // For verify endpoint, don't throw errors - just return unauthenticated
      return { authenticated: false, user: undefined };
    }
  }

  async getUserProfile(): Promise<AdminUser> {
    const result = await this.fetchWithAuth<AdminUser>('/auth/profile');
    return result.data;
  }

  async logout(): Promise<void> {
    try {
      // Step 1: Call logout endpoint to invalidate server-side session
      await this.fetchWithAuth('/auth/logout', { method: 'POST' });
      
      // Step 2: Clear all local storage data
      localStorage.clear();
      
      // Step 3: Clear session storage
      sessionStorage.clear();
      
      // Step 4: Clear any cached API data
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
      
      // Step 5: Revoke any service worker registrations
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
      }
      
      // Step 6: Clear any IndexedDB data
      if ('indexedDB' in window) {
        try {
          const databases = await indexedDB.databases();
          await Promise.all(
            databases.map(db => {
              if (db.name) {
                const deleteRequest = indexedDB.deleteDatabase(db.name);
                return new Promise((resolve, reject) => {
                  deleteRequest.onsuccess = () => resolve(void 0);
                  deleteRequest.onerror = () => reject(deleteRequest.error);
                });
              }
            })
          );
        } catch (error) {
          console.warn('Could not clear IndexedDB:', error);
        }
      }
      
      console.log('✅ Complete logout cleanup performed');
    } catch (error) {
      console.error('❌ Logout cleanup error:', error);
      // Still proceed with logout even if cleanup fails
    }
  }

  /**
   * User Management APIs
   */
  async getUsers(): Promise<AdminUser[]> {
    const result = await this.fetchWithAuth<AdminUser[]>('/auth/users');
    return result.data;
  }

  async addUser(userData: AddUserRequest): Promise<AddUserResponse> {
    const result = await this.fetchWithAuth<AddUserResponse>('/auth/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return result.data;
  }

  async updateUserRole(userId: number, role: string): Promise<AdminUser> {
    const result = await this.fetchWithAuth<AdminUser>(`/auth/users/${userId}/role`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    });
    return result.data;
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<AdminUser> {
    const result = await this.fetchWithAuth<AdminUser>(`/auth/users/${userId}/status`, {
      method: 'POST',
      body: JSON.stringify({ is_active: isActive }),
    });
    return result.data;
  }

  /**
   * Update user permissions with granular permission array
   * V4.162: New method for granular permission system
   */
  async updateUserPermissions(userId: string, permissions: import('../types/auth').Permission[]): Promise<AdminUser> {
    const result = await this.fetchWithAuth<AdminUser>(`/api/admin/user-management/admin-users/${userId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    });
    return result.data;
  }

  /**
   * Dashboard APIs
   */

  async getRevenueData(period: '7d' | '30d' | '90d' = '30d') {
    const result = await this.fetchWithAuth(`/api/dashboard/revenue-trends?period=${period}`);
    return result.data;
  }

  async getRevenueTrends(timeRange: string) {
    const result = await this.fetchWithAuth(`/api/dashboard/revenue-trends?period=${timeRange}`);
    return result;
  }

  async getProducts() {
    const result = await this.fetchWithAuth('/api/dashboard/products');
    return result;
  }

  async getSubscriptions() {
    const result = await this.fetchWithAuth('/api/dashboard/subscriptions');
    return result;
  }

  async getFinancialData() {
    const result = await this.fetchWithAuth('/api/dashboard/financial');
    return result;
  }

  async getCustomerData() {
    const result = await this.fetchWithAuth('/api/dashboard/customers');
    return result.data;
  }

  async getSubscriptionData() {
    const result = await this.fetchWithAuth('/api/dashboard/subscriptions');
    return result.data;
  }

  /**
   * Audit Log APIs
   */
  async getAuditLog(page = 1, limit = 50): Promise<{ entries: AuditLogEntry[]; total: number; page: number }> {
    const result = await this.fetchWithAuth<{ entries: AuditLogEntry[]; total: number; page: number }>(
      `/auth/audit-log?page=${page}&limit=${limit}`
    );
    return result.data;
  }

  /**
   * Notification APIs
   */
  async getNotificationOverview() {
    const result = await this.fetchWithAuth('/api/admin/notifications/user-tables/overview');
    return result.data;
  }

  async getNotificationCampaigns() {
    const result = await this.fetchWithAuth('/api/admin/notifications/campaigns');
    return result.data;
  }

  /**
   * Monitoring APIs
   */

  // Real-time AWS monitoring endpoints
  async getAWSRealTimeMetrics() {
    const result = await this.fetchWithAuth('/api/admin/aws-monitor/realtime');
    return result;
  }

  async getAWSAlarms() {
    const result = await this.fetchWithAuth('/api/admin/aws-monitor/alarms');
    return result;
  }

  // @deprecated Use getIntegratedAIAWSDashboard() instead
  async getAWSCostForecast() {
    console.warn('⚠️ getAWSCostForecast is deprecated. Use getIntegratedAIAWSDashboard() instead.');
    const result = await this.fetchWithAuth('/api/admin/aws-monitor/costs/forecast');
    return result;
  }

  /**
   * CloudWatch Logs APIs
   */
  async getCloudWatchLogsRecent(params: { limit?: number; severity?: string; service?: string; timeRange?: string; search?: string }) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.severity) queryParams.set('severity', params.severity);
    if (params.service) queryParams.set('service', params.service);
    if (params.timeRange) queryParams.set('timeRange', params.timeRange);
    if (params.search) queryParams.set('search', params.search);

    return this.fetchWithAuth(`/api/admin/cloudwatch/logs/recent?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  async getCloudWatchLogsErrors(params: { limit?: number; timeRange?: string; service?: string }) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.timeRange) queryParams.set('timeRange', params.timeRange);
    if (params.service) queryParams.set('service', params.service);

    return this.fetchWithAuth(`/api/admin/cloudwatch/logs/errors?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  async getCloudWatchLogsSecurity(params: { limit?: number; timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.timeRange) queryParams.set('timeRange', params.timeRange);

    return this.fetchWithAuth(`/api/admin/cloudwatch/logs/security?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  async getCloudWatchLogsStats(params: { timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.set('timeRange', params.timeRange);

    return this.fetchWithAuth(`/api/admin/cloudwatch/logs/stats?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  async getCloudWatchAlarms(params?: { timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.timeRange) {
      queryParams.set('timeRange', params.timeRange);
    }
    const queryString = queryParams.toString();
    return this.fetchWithAuth(
      `/api/admin/ecs-monitor/alarms${queryString ? '?' + queryString : ''}`
    );
  }

  async getCloudWatchLogsPM2(params: { service: string; limit?: number; logType?: string }) {
    const queryParams = new URLSearchParams();
    queryParams.set('service', params.service);
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.logType) queryParams.set('logType', params.logType);

    return this.fetchWithAuth(`/api/admin/cloudwatch/logs/pm2?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  async getGrokAIOverview() {
    const result = await this.fetchWithAuth('/api/admin/grok-ai/overview?timeRange=last7days');
    return result.data;
  }

  // ===================================================================
  // Website Monitoring API Methods
  // ===================================================================

  /**
   * Get website monitoring dashboard with Route53 health checks, DNS status, SSL certs, and alarms
   * @param timeRange Optional time range (e.g., '24h', '7d')
   */
  async getWebsiteMonitorDashboard(params?: { timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.timeRange) queryParams.set('timeRange', params.timeRange);

    return this.fetchWithAuth(`/api/admin/website-monitor?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get Route53 health check statuses
   * @param timeRange Optional time range
   */
  async getWebsiteHealthChecks(params?: { timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.timeRange) queryParams.set('timeRange', params.timeRange);

    return this.fetchWithAuth(`/api/admin/website-monitor/health-checks?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get DNS query metrics
   * @param timeRange Optional time range
   */
  async getWebsiteDNSMetrics(params?: { timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.timeRange) queryParams.set('timeRange', params.timeRange);

    return this.fetchWithAuth(`/api/admin/website-monitor/dns-metrics?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get SSL certificate status
   */
  async getWebsiteSSLStatus() {
    return this.fetchWithAuth('/api/admin/website-monitor/ssl-status', {
      method: 'GET',
    });
  }

  /**
   * Get active website/DNS alarms
   * @param timeRange Optional time range
   */
  async getWebsiteAlarms(params?: { timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.timeRange) queryParams.set('timeRange', params.timeRange);

    return this.fetchWithAuth(`/api/admin/website-monitor/alarms?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  // =====================================
  // ECS (Elastic Container Service) Monitor Methods
  // =====================================

  /**
   * Get comprehensive ECS monitoring dashboard
   * @param params Optional parameters including timeRange
   */
  async getECSMonitorDashboard(params?: { timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.timeRange) queryParams.set('timeRange', params.timeRange);

    return this.fetchWithAuth(`/api/admin/ecs-monitor?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get all ECS clusters with status and capacity info
   */
  async getECSClusters() {
    return this.fetchWithAuth('/api/admin/ecs-monitor/clusters', {
      method: 'GET',
    });
  }

  /**
   * Get ECS services across clusters or for a specific cluster
   * @param params Optional parameters including clusterName
   */
  async getECSServices(params?: { clusterName?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.clusterName) queryParams.set('clusterName', params.clusterName);

    return this.fetchWithAuth(`/api/admin/ecs-monitor/services?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get ECS tasks (active, stopped, failed)
   * @param params Optional parameters including clusterName, serviceName, status
   */
  async getECSTasks(params?: { clusterName?: string; serviceName?: string; status?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.clusterName) queryParams.set('clusterName', params.clusterName);
    if (params?.serviceName) queryParams.set('serviceName', params.serviceName);
    if (params?.status) queryParams.set('status', params.status);

    return this.fetchWithAuth(`/api/admin/ecs-monitor/tasks?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get active ECS deployments
   */
  async getECSDeployments() {
    return this.fetchWithAuth('/api/admin/ecs-monitor/deployments', {
      method: 'GET',
    });
  }

  /**
   * Get ECS-related CloudWatch alarms
   * @param params Optional parameters including timeRange
   */
  async getECSAlarms(params?: { timeRange?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.timeRange) queryParams.set('timeRange', params.timeRange);

    return this.fetchWithAuth(`/api/admin/ecs-monitor/alarms?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get website endpoint status (Route53 health checks)
   */
  async getEndpointStatus() {
    const result = await this.fetchWithAuth('/api/admin/website-monitor/endpoints');
    return result.data;
  }

  /**
   * Get visitor geographic distribution data
   */
  async getGeographicData(timeRange: string) {
    const result = await this.fetchWithAuth(`/api/admin/website-monitor/geographic?timeRange=${timeRange}`);
    return result.data;
  }

  /**
   * Get website performance chart data
   */
  async getWebsiteChart(timeRange: string) {
    const result = await this.fetchWithAuth(`/api/admin/website-monitor/chart?timeRange=${timeRange}`);
    return result.data;
  }

  /**
   * Get website health metrics (uptime, SSL, response time)
   */
  async getWebsiteMetrics() {
    const result = await this.fetchWithAuth('/api/admin/website-monitor/metrics');
    return result.data;
  }

  /**
   * Get website performance analytics (traffic, visitors, engagement)
   */
  async getWebsitePerformance(timeRange: string) {
    const result = await this.fetchWithAuth(`/api/admin/website-monitor/performance?timeRange=${timeRange}`);
    return result.data;
  }

  // =====================================
  // API Health Monitor Methods
  // =====================================

  async getApiHealthDashboard() {
    return this.fetchWithAuth('/api/admin/api-monitor', { method: 'GET' });
  }

  async getApiHealthChecks() {
    return this.fetchWithAuth('/api/admin/api-monitor/health-checks', { method: 'GET' });
  }

  async getApiHealthHistory(timeRange: string) {
    return this.fetchWithAuth(`/api/admin/api-monitor/history?timeRange=${timeRange}`, { method: 'GET' });
  }

  async getApiUptime() {
    return this.fetchWithAuth('/api/admin/api-monitor/uptime', { method: 'GET' });
  }

  async getApiIncidents() {
    return this.fetchWithAuth('/api/admin/api-monitor/incidents', { method: 'GET' });
  }

  async getApiActiveIncidents() {
    return this.fetchWithAuth('/api/admin/api-monitor/incidents/active', { method: 'GET' });
  }

  async acknowledgeApiIncident(id: string, notes?: string) {
    return this.fetchWithAuth(`/api/admin/api-monitor/incidents/${id}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  // Missing Performance Monitor methods
  async getPerformanceMetrics() {
    try {
      const result = await this.fetchWithAuth('/api/performance/metrics');
      return result.data;
    } catch (error) {
      // Return mock data for development
      return {
        cpu: { usage: 45.2, cores: 8, frequency: 3.2 },
        memory: { used: 8.4, total: 16, usage: 52.5 },
        disk: { used: 245, total: 512, usage: 47.8 },
        network: { incoming: 125.6, outgoing: 87.3 },
        uptime: 7.5
      };
    }
  }

  async getTopProcesses() {
    try {
      const result = await this.fetchWithAuth('/api/performance/processes');
      return result.data;
    } catch (error) {
      // Return mock data for development
      return [
        { name: 'node', cpu: 12.5, memory: 156.7, pid: 1234 },
        { name: 'postgres', cpu: 8.2, memory: 98.4, pid: 5678 },
        { name: 'redis-server', cpu: 2.1, memory: 45.2, pid: 9012 },
        { name: 'nginx', cpu: 1.8, memory: 23.1, pid: 3456 }
      ];
    }
  }

  async getOptimizationSuggestions() {
    try {
      const result = await this.fetchWithAuth('/api/performance/optimize');
      return result.data;
    } catch (error) {
      // Return mock data for development
      return [
        { category: 'Memory', suggestion: 'Consider increasing connection pooling for database', impact: 'Medium' },
        { category: 'CPU', suggestion: 'Enable response compression for static assets', impact: 'Low' },
        { category: 'Disk', suggestion: 'Implement log rotation to free up disk space', impact: 'High' }
      ];
    }
  }

  async getPerformanceChart() {
    try {
      const result = await this.fetchWithAuth('/api/performance/chart');
      return result.data;
    } catch (error) {
      // Return mock data for development
      return {
        labels: Array.from({length: 24}, (_, i) => `${i}:00`),
        datasets: [
          {
            label: 'CPU Usage (%)',
            data: Array.from({length: 24}, () => Math.floor(Math.random() * 40) + 30),
            borderColor: '#FF6B6B',
            backgroundColor: 'rgba(255, 107, 107, 0.1)'
          },
          {
            label: 'Memory Usage (%)',
            data: Array.from({length: 24}, () => Math.floor(Math.random() * 30) + 40),
            borderColor: '#4ECDC4',
            backgroundColor: 'rgba(78, 205, 196, 0.1)'
          }
        ]
      };
    }
  }

  async getAIQueueData() {
    const result = await this.fetchWithAuth('/api/admin/ai-queue?limit=10&timeRange=last7days');
    return result.data;
  }

  /**
   * System Health APIs
   */

  /**
   * Family Collaboration APIs
   */
  async getFamilyOverview() {
    const result = await this.fetchWithAuth('/api/admin/family-collaboration/overview');
    return result;
  }

  async getFamilyEngagementChart(period: 'weekly' | 'monthly' = 'weekly') {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/engagement-chart?period=${period}`);
    return result;
  }

  async getTopFamilies() {
    const result = await this.fetchWithAuth('/api/admin/family-collaboration/top-families');
    return result;
  }

  async getFamilyModerationOverview() {
    const result = await this.fetchWithAuth('/api/admin/family-collaboration/moderation');
    return result;
  }

  async getFamilyRecentActivities() {
    const result = await this.fetchWithAuth('/api/admin/family-collaboration/recent-activities');
    return result;
  }

  async getFamilyGroups() {
    const result = await this.fetchWithAuth('/api/admin/family-collaboration/families');
    return result;
  }

  async getFamilyMembers(familyId: string) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/families/${familyId}/members`);
    return result;
  }

  async getFamilyInvitations(familyId: string) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/families/${familyId}/invitations`);
    return result;
  }

  async inviteFamilyMember(familyId: string, inviteData: { email: string; role: string; message?: string }) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/families/${familyId}/invite`, {
      method: 'POST',
      body: JSON.stringify(inviteData),
    });
    return result;
  }

  async updateFamilyMember(familyId: string, memberId: string, memberData: { role: string; nickname?: string; bio?: string; is_active: boolean }) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/families/${familyId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify(memberData),
    });
    return result;
  }

  async removeFamilyMember(familyId: string, memberId: string) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/families/${familyId}/members/${memberId}`, {
      method: 'DELETE',
    });
    return result;
  }

  async getFamilyAnalytics() {
    const result = await this.fetchWithAuth('/api/family-collaboration/analytics/overview');
    return result.data;
  }

  async getFamilySharedContent() {
    const result = await this.fetchWithAuth('/api/admin/family-collaboration/shared-content');
    return result;
  }

  // `timeframe` is today|3d|7d|14d|30d|90d|custom; a custom window also needs from&to (YYYY-MM-DD).
  async getDigestAnalytics(timeframe: string = '30d', from?: string, to?: string) {
    const params = new URLSearchParams({ timeframe });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/digest-analytics?${params.toString()}`);
    return result;
  }

  async getContentPermissions(contentId: string) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/content/${contentId}/permissions`);
    return result;
  }

  async shareContentWithFamily(contentData: { 
    content_type: string; 
    content_id: string; 
    family_id: string; 
    title: string; 
    description?: string; 
    permissions: { view: boolean; comment: boolean; edit: boolean }; 
    is_featured: boolean 
  }) {
    const result = await this.fetchWithAuth('/api/admin/family-collaboration/share-content', {
      method: 'POST',
      body: JSON.stringify(contentData),
    });
    return result;
  }

  async updateSharedContent(contentId: string, contentData: { 
    title: string; 
    description?: string; 
    permissions: { view: boolean; comment: boolean; edit: boolean }; 
    is_featured: boolean 
  }) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/shared-content/${contentId}`, {
      method: 'PUT',
      body: JSON.stringify(contentData),
    });
    return result;
  }

  async toggleContentFeatured(contentId: string, isFeatured: boolean) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/shared-content/${contentId}/featured`, {
      method: 'PUT',
      body: JSON.stringify({ is_featured: isFeatured }),
    });
    return result;
  }

  async removeSharedContent(contentId: string) {
    const result = await this.fetchWithAuth(`/api/admin/family-collaboration/shared-content/${contentId}`, {
      method: 'DELETE',
    });
    return result;
  }

  /**
   * Content Management APIs
   */
  
  // Legacy Quests
  async getLegacyQuests(filters?: {
    level?: string;
    category?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, value.toString());
      });
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const result = await this.fetchWithAuth(`/api/admin/content/legacy-quests${query}`);
    return result.data;
  }

  async createLegacyQuest(questData: any) {
    const result = await this.fetchWithAuth('/api/admin/content/legacy-quests', {
      method: 'POST',
      body: JSON.stringify(questData),
    });
    return result.data;
  }

  async updateLegacyQuest(questId: number, questData: any) {
    const result = await this.fetchWithAuth(`/api/admin/content/legacy-quests/${questId}`, {
      method: 'PUT',
      body: JSON.stringify(questData),
    });
    return result.data;
  }

  async deleteLegacyQuest(questId: number) {
    const result = await this.fetchWithAuth(`/api/admin/content/legacy-quests/${questId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async getLegacyQuestLevels() {
    const result = await this.fetchWithAuth('/api/admin/content/legacy-quests/levels');
    return result?.data || { levels: [1, 2, 3, 4, 5, 6, 7] };
  }

  async getLegacyQuestCategories() {
    const result = await this.fetchWithAuth('/api/admin/content/legacy-quests/categories');
    return result?.data || { categories: [] };
  }

  async getLegacyQuestDropdownOptions() {
    const result = await this.fetchWithAuth('/api/admin/content/legacy-quests/dropdown-options');
    return result.data;
  }

  async exportLegacyQuests(format: 'csv' | 'json' = 'csv') {
    const response = await fetch(`${this.baseUrl}/api/admin/content/legacy-quests/export?format=${format}`, {
      credentials: 'include',
    });
    return response.blob();
  }

  async getLegacyQuestAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/cms/legacy-quests/analytics/overview');
    return result.data;
  }

  // Daily Inspirations
  async getDailyInspirations(filters?: {
    category?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, value.toString());
      });
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const result = await this.fetchWithAuth(`/api/admin/cms/daily-inspirations${query}`);
    return result.data;
  }

  async getDailyInspirationAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/cms/daily-inspirations/analytics/overview');
    return result.data;
  }

  async createDailyInspiration(inspirationData: any) {
    const result = await this.fetchWithAuth('/api/admin/cms/daily-inspirations', {
      method: 'POST',
      body: JSON.stringify(inspirationData),
    });
    return result.data;
  }

  async updateDailyInspiration(inspirationId: number, inspirationData: any) {
    const result = await this.fetchWithAuth(`/api/admin/cms/daily-inspirations/${inspirationId}`, {
      method: 'PUT',
      body: JSON.stringify(inspirationData),
    });
    return result.data;
  }

  async deleteDailyInspiration(inspirationId: number) {
    const result = await this.fetchWithAuth(`/api/admin/cms/daily-inspirations/${inspirationId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  // Legacy methods for backward compatibility
  async getInspirations(filters?: {
    category?: string;
    published?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, value.toString());
      });
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const result = await this.fetchWithAuth(`/api/admin/content/inspirations${query}`);
    return result.data;
  }

  async getTodaysInspiration() {
    const result = await this.fetchWithAuth('/api/inspirations/today');
    return result.data;
  }

  async getInspirationCycleStatus() {
    const result = await this.fetchWithAuth('/api/admin/inspirations/cycle/status');
    return result;
  }

  async createInspiration(inspirationData: any) {
    const result = await this.fetchWithAuth('/api/admin/content/inspirations', {
      method: 'POST',
      body: JSON.stringify(inspirationData),
    });
    return result.data;
  }

  async updateInspiration(inspirationId: number, inspirationData: any) {
    const result = await this.fetchWithAuth(`/api/admin/content/inspirations/${inspirationId}`, {
      method: 'PUT',
      body: JSON.stringify(inspirationData),
    });
    return result.data;
  }

  async deleteInspiration(inspirationId: number) {
    const result = await this.fetchWithAuth(`/api/admin/content/inspirations/${inspirationId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async resetInspirationCycle() {
    const result = await this.fetchWithAuth('/api/admin/inspirations/cycle/reset', {
      method: 'POST',
    });
    return result;
  }

  async getInspirationUsageOverview() {
    const result = await this.fetchWithAuth('/api/admin/inspirations/usage/overview');
    return result;
  }

  async getTopInspirations() {
    const result = await this.fetchWithAuth('/api/admin/inspirations/usage/top');
    return result;
  }

  async exportInspirations(format: 'csv' | 'json' = 'csv') {
    const response = await fetch(`${this.baseUrl}/api/admin/content/inspirations/export?format=${format}`, {
      credentials: 'include',
    });
    return response.blob();
  }

  // Chapter Management
  async getChapters(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, value.toString());
      });
    }
    // Backend expects offset/limit; UI sends page/limit
    if (filters?.page !== undefined && filters?.limit !== undefined) {
      const p = filters.page;
      const lim = filters.limit;
      params.delete('page');
      params.set('offset', String(Math.max(0, (p - 1) * lim)));
      params.set('limit', String(lim));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    type ChapterCreationsPayload = {
      chapters?: any[];
      totalCount?: number;
      pagination?: ApiPagination;
    };
    const result = await this.fetchWithAuth<ChapterCreationsPayload>(`/api/admin/cms/chapter-creations${query}`);

    // Transform the response to match the frontend expectations
    console.log('🔍 API Service - Raw chapter data:', result.data);

    if (result.data && result.data.chapters) {
      const transformedChapters = result.data.chapters.map((chapter: any) => {
        console.log('🔍 Transforming chapter:', chapter);
        const uid = chapter.userId ?? chapter.user_id;
        const email = chapter.userEmail ?? chapter.user_email;
        const fn = chapter.firstName ?? chapter.first_name;
        const ln = chapter.lastName ?? chapter.last_name;
        const displayName =
          fn && ln ? `${fn} ${ln}`.trim() : (fn || ln || email || 'Unknown');
        const bookId = chapter.bookId ?? chapter.book_id ?? null;
        const bookTitleRaw = chapter.bookTitle ?? chapter.book_title;
        return {
          id: chapter.id,
          title: chapter.title,
          content: chapter.content,
          status: chapter.status,
          created: chapter.created_at,
          updated: chapter.updated_at,
          wordCount: chapter.word_count || 0,
          aiProcessed: chapter.transformation_count > 0,
          processingTime: chapter.ai_processing_cost_usd ? Math.round(chapter.ai_processing_cost_usd * 100) : 0,
          user: {
            id: uid,
            email: email || '',
            name: displayName
          },
          book: {
            id: bookId,
            title: bookTitleRaw && String(bookTitleRaw).trim() ? bookTitleRaw : 'No Book Assigned'
          },
          writingStyle: chapter.writing_style || null,
          illustrationStyle: chapter.illustration_style || null,
          mood: chapter.mood || null,
        };
      });
      
      console.log('🔍 Transformed chapters:', transformedChapters);
      
      return {
        chapters: transformedChapters,
        totalCount: result.data.totalCount || result.pagination?.total || 0,
        pagination: result.data.pagination
      };
    }
    
    return result.data;
  }

  async getChapterAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/cms/chapter-creations/analytics/overview');
    return result.data;
  }

  async getChapterById(chapterId: string) {
    const result = await this.fetchWithAuth(`/api/admin/cms/chapter-creations/${chapterId}`);
    return result.data;
  }

  async deleteChapter(chapterId: string) {
    const result = await this.fetchWithAuth(`/api/admin/cms/chapter-creations/${chapterId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  // Quest Management
  async getQuests(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    userId?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      // Map page to offset for the backend API
      if (filters.page !== undefined && filters.limit !== undefined) {
        const offset = (filters.page) * filters.limit;
        params.append('offset', offset.toString());
        params.append('limit', filters.limit.toString());
      }
      if (filters.search) params.append('search', filters.search);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.status) params.append('status', filters.status);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const result = await this.fetchWithAuth(`/api/admin/cms/quest-creations${query}`);

    // Backend returns { success, data: quest[], pagination }. fetchWithAuth unwraps so result.data = quest[] and result.pagination = pagination.
    // Handle both: result.data as array (unwrapped) or result.data as object with .data array (if unwrap ever disabled).
    const rawList: unknown[] | null = Array.isArray(result.data)
      ? result.data
      : (Array.isArray((result as any).data?.data) ? (result as any).data.data : null);
    const pagination = result.pagination ?? (result.data && typeof result.data === 'object' && !Array.isArray(result.data) ? (result.data as any).pagination : undefined);
    const total = pagination?.total ?? (Array.isArray(rawList) ? rawList.length : 0);

    if (Array.isArray(rawList)) {
      const transformedQuests = rawList.map((quest: any) => ({
        id: quest.id,
        title: quest.title,
        story: quest.story ?? quest.content ?? '',
        originalText: quest.originalText ?? quest.description ?? '',
        status: quest.status,
        created: quest.createdAt ?? quest.created ?? quest.created_at,
        updated: quest.updatedAt ?? quest.updated ?? quest.updated_at,
        wordCount: quest.wordCount ?? quest.word_count ?? 0,
        writingStyle: quest.writingStyle ?? quest.writing_style,
        illustrationStyle: quest.illustrationStyle ?? quest.illustration_style,
        mood: quest.mood,
        user: {
          id: quest.userId ?? quest.user_id ?? quest.user?.id,
          email: quest.userEmail ?? quest.user_email ?? quest.user?.email,
          subscriptionPlan: quest.subscriptionPlan ?? quest.subscription_plan ?? quest.user?.subscriptionPlan
        },
        questPromptId: quest.questPromptId ?? quest.quest_prompt_id ?? quest.questPromptId,
        source: quest.source || 'quest'
      }));

      return {
        quests: transformedQuests,
        totalCount: total,
        pagination: pagination ?? null
      };
    }

    return { quests: [], totalCount: 0, pagination: null };
  }

  async getQuestAnalytics() {
    const result = await this.fetchWithAuth<Record<string, unknown>>('/api/admin/cms/quest-analytics');
    const d = result.data;
    return (d && typeof d === 'object' && 'analytics' in d ? (d as { analytics?: unknown }).analytics : undefined) || d;
  }

  async getQuestById(questId: string) {
    const result = await this.fetchWithAuth<Record<string, unknown>>(`/api/admin/cms/quest-creations/${questId}`);
    const d = result.data;
    return (d && typeof d === 'object' && 'data' in d ? (d as { data?: unknown }).data : undefined) || d;
  }

  async deleteQuest(questId: string) {
    const result = await this.fetchWithAuth(`/api/admin/cms/quest-creations/${questId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  // Legacy method for backward compatibility
  async getChapterCreationsOverview(timeRange?: string) {
    const params = timeRange ? `?timeRange=${timeRange}` : '';
    const result = await this.fetchWithAuth(`/api/admin/cms/chapter-creations/analytics/overview${params}`);
    return result.data;
  }

  // Detailed chapter analytics (Chart.js analytics tab)
  async getDetailedChapterAnalytics(timeRange?: string) {
    const params = timeRange ? `?timeRange=${timeRange}` : '';
    const result = await this.fetchWithAuth<Record<string, unknown>>(`/api/admin/cms/chapter-creations/analytics${params}`);
    const d = result.data;
    return (d && typeof d === 'object' && 'analytics' in d ? (d as { analytics?: unknown }).analytics : undefined) || d;
  }

  // Detailed quest analytics (Chart.js analytics tab)
  async getDetailedQuestAnalytics(timeRange?: string) {
    const params = timeRange ? `?timeRange=${timeRange}` : '';
    const result = await this.fetchWithAuth<Record<string, unknown>>(`/api/admin/cms/quest-creations/analytics${params}`);
    const d = result.data;
    return (d && typeof d === 'object' && 'analytics' in d ? (d as { analytics?: unknown }).analytics : undefined) || d;
  }

  /**
   * AI Management APIs
   */
  
  // AI Styles
  async getAIStyles() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles');
    return result.data;
  }

  async createAIStyle(styleData: any) {
    const result = await this.fetchWithAuth('/api/admin/ai-styles', {
      method: 'POST',
      body: JSON.stringify(styleData),
    });
    return result.data;
  }

  async updateAIStyle(styleId: number, styleData: any) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/${styleId}`, {
      method: 'PUT',
      body: JSON.stringify(styleData),
    });
    return result.data;
  }

  async deleteAIStyle(styleId: number) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/${styleId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  // Mood Management
  async getMoods() {
    const result = await this.fetchWithAuth('/api/admin/moods');
    return result.data;
  }

  async createMood(moodData: any) {
    const result = await this.fetchWithAuth('/api/admin/moods', {
      method: 'POST',
      body: JSON.stringify(moodData),
    });
    return result.data;
  }

  async updateMood(moodId: number, moodData: any) {
    const result = await this.fetchWithAuth(`/api/admin/moods/${moodId}`, {
      method: 'PUT',
      body: JSON.stringify(moodData),
    });
    return result.data;
  }

  async deleteMood(moodId: number) {
    const result = await this.fetchWithAuth(`/api/admin/moods/${moodId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  /**
   * Marketing Intelligence APIs
   */
  async getMarketingDashboard() {
    const result = await this.fetchWithAuth('/api/admin/marketing-dashboard');
    return result.data;
  }

  async getPlayerDashboard() {
    const result = await this.fetchWithAuth('/api/admin/player-dashboard');
    return result.data;
  }

  async getRevenueDashboard() {
    const result = await this.fetchWithAuth('/api/dashboard/overview');
    return result.data;
  }

  async getIntegrationStatus() {
    const result = await this.fetchWithAuth('/api/admin/integration-status');
    return result.data;
  }

  /**
   * Location Sharing APIs
   */
  async getLocationSharingUsers() {
    const result = await this.fetchWithAuth('/api/location-sharing/users');
    return result.data;
  }

  async getLocationAnalytics() {
    const result = await this.fetchWithAuth('/api/location-sharing/analytics');
    return result.data;
  }

  /**
   * Messaging APIs
   */
  async getInAppMessages(filters?: {
    status?: string;
    dateRange?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, value.toString());
      });
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const result = await this.fetchWithAuth(`/api/messaging/in-app${query}`);
    return result.data;
  }

  async sendInAppMessage(messageData: any) {
    const result = await this.fetchWithAuth('/api/messaging/in-app/send', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
    return result.data;
  }

  async getSMSMessages(filters?: {
    status?: string;
    dateRange?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, value.toString());
      });
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    const result = await this.fetchWithAuth(`/api/messaging/sms${query}`);
    return result.data;
  }

  async sendSMSMessage(messageData: any) {
    const result = await this.fetchWithAuth('/api/messaging/sms/send', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
    return result.data;
  }

  /**
   * User Tables APIs
   */
  async getUserTables() {
    const result = await this.fetchWithAuth('/api/admin/user-tables');
    return result.data;
  }

  async getUserTablesAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/user-tables/analytics');
    return result.data;
  }

  async exportUserTables(format: 'csv' | 'json' = 'csv') {
    const response = await fetch(`${this.baseUrl}/api/admin/user-tables/export?format=${format}`, {
      credentials: 'include',
    });
    return response.blob();
  }

  /**
   * Utility Methods
   */
  async downloadFile(url: string, filename: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      credentials: 'include',
    });
    const blob = await response.blob();
    
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  async uploadFile(endpoint: string, file: File, additionalData?: any) {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
    }

    const result = await this.fetchWithAuth(endpoint, {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
    return result.data;
  }

  /**
   * Real-time Updates
   */
  setupWebSocket(): WebSocket | null {
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/webhooks/websocket`);
      
      ws.onopen = () => {
        console.log('WebSocket connected for real-time updates');
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      return ws;
    } catch (error) {
      console.error('Failed to setup WebSocket:', error);
      return null;
    }
  }

  /**
   * In-App Messaging APIs
   */
  async getInAppMessagingMetrics() {
    try {
      const result = await this.fetchWithAuth('/api/admin/cms/notifications/in-app-messaging/metrics');
      return result.data;
    } catch (error) {
      console.warn('In-app messaging metrics endpoint not available, using fallback');
      return {
        total_sent: 1234,
        delivered: 1180,
        opened: 856,
        open_rate: 72.5,
        active_subscribers: 1500,
        growth_rate: 12.5
      };
    }
  }

  async getNotificationHistory() {
    try {
      const result = await this.fetchWithAuth('/api/admin/cms/notifications/in-app-messaging/history');
      return result.data;
    } catch (error) {
      console.warn('Notification history endpoint not available, using fallback');
      return {
        notifications: [
          {
            id: '1',
            title: 'Welcome to LifeScribe',
            message: 'Start your memory journey today',
            status: 'sent',
            sentAt: new Date().toISOString(),
            deliveredCount: 245,
            openedCount: 178
          },
          {
            id: '2',
            title: 'New Feature Available',
            message: 'Check out our latest AI enhancements',
            status: 'delivered',
            sentAt: new Date(Date.now() - 86400000).toISOString(),
            deliveredCount: 312,
            openedCount: 234
          }
        ],
        total: 25,
        page: 1
      };
    }
  }

  async sendPushNotification(notificationData: {
    title: string;
    message: string;
    target_audience: string;
    timing: string;
    scheduled_time?: string | null;
  }) {
    const result = await this.fetchWithAuth('/api/admin/cms/notifications/in-app-messaging/send', {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
    return result.data;
  }

  // New Push Notification Management APIs
  async getPushNotifications(params?: {
    page?: number;
    limit?: number;
    status?: string;
    target_audience?: string;
    date_from?: string;
    date_to?: string;
  }) {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) query.append(key, value.toString());
      });
    }
    const result = await this.fetchWithAuth(`/api/admin/push-notifications/notifications?${query.toString()}`);
    return result.data;
  }

  async getPushNotification(id: string) {
    const result = await this.fetchWithAuth(`/api/admin/push-notifications/notifications/${id}`);
    return result.data;
  }

  async getPushNotificationAnalytics(period: string = '30d') {
    const result = await this.fetchWithAuth(`/api/admin/push-notifications/analytics/overview?period=${period}`);
    return result.data;
  }

  async registerDeviceToken(deviceData: {
    user_id: string // UUID;
    device_token: string;
    platform: string;
    app_version?: string;
    os_version?: string;
    device_model?: string;
    language?: string;
    timezone?: string;
    push_enabled?: boolean;
  }) {
    const result = await this.fetchWithAuth('/api/admin/push-notifications/devices/register', {
      method: 'POST',
      body: JSON.stringify(deviceData),
    });
    return result.data;
  }

  async getUserDeviceTokens(userId: number, activeOnly: boolean = true) {
    const result = await this.fetchWithAuth(`/api/admin/push-notifications/devices/user/${userId}?active_only=${activeOnly}`);
    return result.data;
  }

  async removeDeviceToken(deviceToken: string) {
    const result = await this.fetchWithAuth(`/api/admin/push-notifications/devices/${deviceToken}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async getPushNotificationTopics() {
    const result = await this.fetchWithAuth('/api/admin/push-notifications/topics');
    return result.data;
  }

  async createPushNotificationTopic(topicData: {
    name: string;
    display_name: string;
    description?: string;
    is_active?: boolean;
    auto_subscribe?: boolean;
    platform?: string[];
  }) {
    const result = await this.fetchWithAuth('/api/admin/push-notifications/topics', {
      method: 'POST',
      body: JSON.stringify(topicData),
    });
    return result.data;
  }

  async subscribeToTopic(topicId: string, subscriptionData: {
    user_ids?: number[];
    device_tokens?: string[];
  }) {
    const result = await this.fetchWithAuth(`/api/admin/push-notifications/topics/${topicId}/subscribe`, {
      method: 'POST',
      body: JSON.stringify(subscriptionData),
    });
    return result.data;
  }

  async testFirebaseConnection() {
    const result = await this.fetchWithAuth('/api/admin/push-notifications/test');
    return result.data;
  }

  async updateNotification(notificationId: string, updates: any) {
    const result = await this.fetchWithAuth(`/api/admin/notifications/in-app-messaging/${notificationId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return result.data;
  }

  async deleteNotification(notificationId: string) {
    const result = await this.fetchWithAuth(`/api/admin/notifications/in-app-messaging/${notificationId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async getInAppNotificationAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/notifications/in-app-messaging/analytics');
    return result.data;
  }

  /**
   * SMS Management APIs
   */
  async getSMSMetrics() {
    const result = await this.fetchWithAuth('/api/admin/notifications/sms/metrics');
    return result.data;
  }

  async getSMSHistory() {
    const result = await this.fetchWithAuth('/api/admin/notifications/sms/history');
    return result.data;
  }

  async getSMSUserSegments() {
    const result = await this.fetchWithAuth('/api/admin/notifications/sms/user-segments');
    return result.data;
  }

  async sendSMS(smsData: {
    message: string;
    recipients: string;
    timing: string;
    scheduled_time?: string | null;
    campaign_name?: string;
  }) {
    const result = await this.fetchWithAuth('/api/admin/notifications/sms/send', {
      method: 'POST',
      body: JSON.stringify(smsData),
    });
    return result.data;
  }

  async updateSMS(smsId: string, updates: any) {
    const result = await this.fetchWithAuth(`/api/admin/notifications/sms/${smsId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return result.data;
  }

  async deleteSMS(smsId: string) {
    const result = await this.fetchWithAuth(`/api/admin/notifications/sms/${smsId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async getSMSAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/notifications/sms/analytics');
    return result.data;
  }

  /**
   * Email Marketing APIs
   */
  async getEmailMarketingMetrics() {
    const result = await this.fetchWithAuth('/api/admin/email-marketing/metrics');
    return result.data;
  }

  async getEmailMarketingCampaigns() {
    const result = await this.fetchWithAuth('/api/admin/email-marketing/campaigns');
    return result.data;
  }
  
  // Alias for getEmailMarketingCampaigns for backward compatibility
  async getEmailCampaigns() {
    return this.getEmailMarketingCampaigns();
  }

  async getEmailTemplates() {
    const result = await this.fetchWithAuth('/api/admin/email-marketing/templates?limit=200');
    return result;
  }

  async getEmailUserSegments() {
    const result = await this.fetchWithAuth('/api/admin/email-marketing/user-segments');
    return result.data;
  }

  async createEmailCampaign(campaignData: {
    campaign_name: string;
    campaign_type: 'marketing' | 'transactional' | 'notification';
    subject_line: string;
    content: string;
    target_user_segment: 'email_notifications' | 'marketing_promotions' | 'all_verified' | 'custom';
    scheduled_send_at?: string | null;
    template_id?: string;
    respect_frequency_limits?: boolean;
    respect_quiet_hours?: boolean;
  }) {
    const result = await this.fetchWithAuth('/api/admin/email-marketing/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });
    // Return full envelope so callers can verify resourceId / server error text
    return result.data;
  }

  async updateEmailCampaign(campaignId: string, updates: any) {
    const result = await this.fetchWithAuth(`/api/admin/email-marketing/campaigns/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return result.data;
  }

  async deleteEmailCampaign(campaignId: string) {
    const result = await this.fetchWithAuth(`/api/admin/email-marketing/campaigns/${campaignId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  /**
   * Send a test copy of a draft/scheduled campaign to the admin user (or an override email).
   * Bypasses segment resolution, frequency caps, and campaign_recipients bookkeeping.
   */
  async sendTestEmailCampaign(campaignId: string, overrideEmail?: string) {
    const result = await this.fetchWithAuth(
      `/api/admin/email-marketing/campaigns/${campaignId}/send-test`,
      {
        method: 'POST',
        body: JSON.stringify(overrideEmail ? { email: overrideEmail } : {}),
      }
    );
    return result.data;
  }

  async createEmailTemplate(templateData: any) {
    const result = await this.fetchWithAuth('/api/admin/email-marketing/templates', {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
    return result.data;
  }

  async updateEmailTemplate(templateId: string, updates: any) {
    const result = await this.fetchWithAuth(`/api/admin/email-marketing/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return result.data;
  }

  async deleteEmailTemplate(templateId: string) {
    const result = await this.fetchWithAuth(`/api/admin/email-marketing/templates/${templateId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async getEmailAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/email-marketing/analytics');
    return result.data;
  }

  async getCommunicationChart() {
    try {
      const result = await this.fetchWithAuth('/api/admin/cms/communications/chart');
      return result.data;
    } catch (error) {
      console.warn('Communication chart endpoint not available, using fallback');
      return {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
          {
            label: 'Email Campaigns',
            data: [45, 52, 38, 61, 42, 55],
            borderColor: '#2196F3',
            backgroundColor: 'rgba(33, 150, 243, 0.1)'
          },
          {
            label: 'SMS Sent',
            data: [28, 35, 42, 31, 48, 39],
            borderColor: '#4CAF50',
            backgroundColor: 'rgba(76, 175, 80, 0.1)'
          },
          {
            label: 'Push Notifications',
            data: [65, 73, 58, 81, 67, 72],
            borderColor: '#FF9800',
            backgroundColor: 'rgba(255, 152, 0, 0.1)'
          }
        ]
      };
    }
  }

  /**
   * Newsletter Subscriber Management APIs
   */
  async getNewsletterSubscribers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    source?: string;
    status?: 'active' | 'unsubscribed';
    converted?: string;
    email_sent?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const result = await this.fetchWithAuth(`/api/admin/newsletter/subscribers${query}`);
    return result.data;
  }

  async getNewsletterSubscriberStats() {
    const result = await this.fetchWithAuth('/api/admin/newsletter/subscribers/stats');
    return result.data;
  }

  async getNewsletterSubscriber(subscriberId: string) {
    const result = await this.fetchWithAuth(`/api/admin/newsletter/subscribers/${subscriberId}`);
    return result.data;
  }

  async updateNewsletterSubscriber(subscriberId: string, updates: {
    converted_to_user?: boolean;
    is_active?: boolean;
    notes?: string;
  }) {
    const result = await this.fetchWithAuth(`/api/admin/newsletter/subscribers/${subscriberId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return result.data;
  }

  async exportNewsletterSubscribers(format: 'csv' | 'json' = 'csv', filters?: any) {
    const queryParams = new URLSearchParams();
    queryParams.append('format', format);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await fetch(`${this.baseUrl}/api/admin/newsletter/subscribers/export?${queryParams.toString()}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  }

  /**
   * Dashboard Overview & System APIs
   */
  async getSystemHealth() {
    try {
      const result = await this.fetchWithAuth('/api/admin/system-health');
      return result.data;
    } catch (error) {
      console.warn('System health endpoint not available, using fallback');
      return {
        status: 'operational',
        uptime: 99.5,
        memory_usage: 65,
        cpu_usage: 45,
        database_status: 'connected',
        active_connections: 150
      };
    }
  }

  async getDashboardOverview() {
    try {
      const result = await this.fetchWithAuth<Record<string, unknown>>('/api/dashboard/overview');

      // Unwrap nested data structure from backend
      // Backend returns: {success: true, data: {overview: {...}}}
      // We need to extract the actual overview object
      const raw = result.data;
      const unwrappedData =
        (raw && typeof raw === 'object' && 'data' in raw ? (raw as { data?: Record<string, unknown> }).data : undefined) ||
        (typeof raw === 'object' && raw !== null ? raw : {}) ||
        {};
      const overviewData =
        (unwrappedData && typeof unwrappedData === 'object' && 'overview' in unwrappedData
          ? (unwrappedData as { overview?: unknown }).overview
          : undefined) || unwrappedData;

      const metrics = result.metrics;

      // Return the full enhanced response including business intelligence metrics
      return {
        // Legacy data for backward compatibility
        legacy: overviewData,

        // New enhanced business intelligence metrics
        metrics: metrics || null,
        charts: result.charts || null,

        // Status information
        partialData: result.partialData || false,
        queryTime: result.queryTime || 0,
        timestamp:
          (unwrappedData && typeof unwrappedData === 'object' && 'last_updated' in unwrappedData
            ? (unwrappedData as { last_updated?: string }).last_updated
            : undefined) ||
          result.timestamp ||
          new Date().toISOString(),

        // Combined format for easy frontend access
        overview: overviewData,
        userMetrics: (metrics?.userMetrics as unknown) ?? null,
        revenueMetrics: (metrics?.revenueMetrics as unknown) ?? null,
        aiCostMetrics: (metrics?.aiCostMetrics as unknown) ?? null,
        chartData: result.charts || null
      };
    } catch (error) {
      console.warn('Dashboard overview endpoint not available, using fallback');
      return {
        legacy: {
          today: { revenue: 2400, transactions: 45, unique_customers: 38 },
          totals: { lifetime_revenue: 156800, total_transactions: 2340, total_customers: 1892 },
          subscriptions: { total_subscriptions: 456, active_subscriptions: 423, current_mrr: 8950 },
          month: { revenue: 24500, transactions: 890, customers: 234, revenue_growth_rate: 12.5, customer_growth_rate: 8.2 },
          content: { total_chapters: 1250, total_users: 892, chapters_last_30_days: 145, users_last_30_days: 67, chapter_authors: 234 },
          comparison: { revenue_change: 15.8, customer_change: 8.2, subscriber_change: 6.7 }
        },
        metrics: null,
        charts: null,
        partialData: true,
        overview: {
          today: { revenue: 2400, transactions: 45, unique_customers: 38 },
          totals: { lifetime_revenue: 156800, total_transactions: 2340, total_customers: 1892 },
          subscriptions: { total_subscriptions: 456, active_subscriptions: 423, current_mrr: 8950 },
          month: { revenue: 24500, transactions: 890, customers: 234, revenue_growth_rate: 12.5, customer_growth_rate: 8.2 },
          content: { total_chapters: 1250, total_users: 892, chapters_last_30_days: 145, users_last_30_days: 67, chapter_authors: 234 },
          comparison: { revenue_change: 15.8, customer_change: 8.2, subscriber_change: 6.7 }
        },
        userMetrics: null,
        revenueMetrics: null,
        aiCostMetrics: null,
        chartData: null
      };
    }
  }

  async getRecentActivity() {
    try {
      const result = await this.fetchWithAuth('/api/admin/integration-status');
      return result.data;
    } catch (error) {
      console.warn('Recent activity endpoint not available, using fallback');
      return [];
    }
  }

  async getCommunicationMetrics() {
    try {
      const result = await this.fetchWithAuth('/api/admin/marketing-dashboard');
      return result.data;
    } catch (error) {
      console.warn('Communication metrics endpoint not available, using fallback');
      return {
        inApp: {
          sent: 0,
          delivered: 0,
          opened: 0,
          clickedThrough: 0,
          openRate: 0,
          ctr: 0
        },
        sms: {
          sent: 0,
          delivered: 0,
          failed: 0,
          deliveryRate: 0,
          cost: 0
        },
        email: {
          sent: 0,
          delivered: 0,
          opened: 0,
          clicked: 0,
          bounced: 0,
          openRate: 0,
          ctr: 0,
          bounceRate: 0
        }
      };
    }
  }

  async getMonitoringDashboard() {
    const result = await this.fetchWithAuth('/api/monitoring/dashboard');
    return result.data;
  }

  async getSystemMetrics() {
    const result = await this.fetchWithAuth('/api/admin/metrics/summary');
    return result.data;
  }

  async getQualityMetrics() {
    const result = await this.fetchWithAuth('/api/quality-monitoring/dashboard');
    return result.data;
  }

  /**
   * Admin Profile Management APIs
   */
  async uploadAdminAvatar(formData: FormData) {
    const result = await this.fetchWithAuth('/auth/upload-avatar', {
      method: 'POST',
      body: formData,
    });
    return result.data;
  }

  async updateAdminProfile(profileData: any) {
    const result = await this.fetchWithAuth('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    return result.data;
  }

  /**
   * AI Styles Management APIs
   * V4.36: Added 4-category separation for zero hardcoding
   */

  // V4.36: NEW - Separated endpoints for 4 categories
  async getAIWritingGenres() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/writing-genres');
    return result.data;
  }

  async getAIWritingAuthors() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/writing-authors');
    return result.data;
  }

  async getAIIllustrationArtStyles() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/illustration-art-styles');
    return result.data;
  }

  async getAIIllustrationArtists() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/illustration-artists');
    return result.data;
  }

  // V4.36: LEGACY - Combined endpoints for backward compatibility
  async getAIWritingStyles() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/writing-styles');
    return result.data;
  }

  async getAIIllustrationStyles() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/illustration-styles');
    return result.data;
  }

  async getAIStylesAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/analytics');
    return result.data;
  }

  async createAIWritingStyle(styleData: any) {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/writing-styles', {
      method: 'POST',
      body: JSON.stringify(styleData),
    });
    return result.data;
  }

  async createAIIllustrationStyle(styleData: any) {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/illustration-styles', {
      method: 'POST',
      body: JSON.stringify(styleData),
    });
    return result.data;
  }

  async updateAIWritingStyle(styleId: number, styleData: any) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/writing-styles/${styleId}`, {
      method: 'PUT',
      body: JSON.stringify(styleData),
    });
    return result.data;
  }

  async updateAIWritingAuthor(authorId: number, styleData: any) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/writing-authors/${authorId}`, {
      method: 'PUT',
      body: JSON.stringify(styleData),
    });
    return result.data;
  }

  async updateAIIllustrationStyle(styleId: number, styleData: any) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/illustration-styles/${styleId}`, {
      method: 'PUT',
      body: JSON.stringify(styleData),
    });
    return result.data;
  }

  async deleteAIWritingStyle(styleId: number) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/writing-styles/${styleId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async deleteAIIllustrationStyle(styleId: number) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/illustration-styles/${styleId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async toggleAIWritingGenre(id: number, isActive: boolean) {
    const result = await this.patch(`/api/admin/ai-styles/writing-genres/${id}/toggle`, { isActive });
    return result.data;
  }
  async toggleAIWritingAuthor(id: number, isActive: boolean) {
    const result = await this.patch(`/api/admin/ai-styles/writing-authors/${id}/toggle`, { isActive });
    return result.data;
  }
  async toggleAIIllustrationStyle(id: number, isActive: boolean) {
    const result = await this.patch(`/api/admin/ai-styles/illustration-art-styles/${id}/toggle`, { isActive });
    return result.data;
  }
  async toggleAIIllustrationArtist(id: number, isActive: boolean) {
    const result = await this.patch(`/api/admin/ai-styles/illustration-artists/${id}/toggle`, { isActive });
    return result.data;
  }

  async reorderAIStyles(category: string, items: Array<{ id: number; sort_order: number }>) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/reorder/${category}`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
    return result.data;
  }

  async getPopularCategorySettings() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/popular-category');
    return result.data;
  }

  async updatePopularCategory(settings: { writing?: string; illustration?: string }) {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/popular-category', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
    return result.data;
  }

  /**
   * AI Model Registry APIs (runtime xAI/Grok model selection)
   * Paths live under /api/admin/ai-styles/xai-models/* (same router as writing-genres) so
   * production routing matches other AI Styles endpoints.
   */
  private static readonly AI_MODEL_REGISTRY_BASE = '/api/admin/ai-styles/xai-models';

  // fetchWithAuth auto-unwraps body.data and drops sibling fields, but /available
  // returns { data, slots, last_synced_at, sync_age_hours, stale } — all top-level.
  // Do a raw fetch so freshness metadata reaches useModelRegistry.applyAvailable.
  async getAvailableModels() {
    const response = await fetch(`${this.baseUrl}${ApiService.AI_MODEL_REGISTRY_BASE}/available`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err.message || `HTTP ${response.status}`);
    }
    return await response.json();
  }

  async getCurrentModels() {
    const result = await this.fetchWithAuth(`${ApiService.AI_MODEL_REGISTRY_BASE}/current`);
    return result.data;
  }

  async updateModelDefault(slot: string, modelId: string, reason?: string) {
    const result = await this.fetchWithAuth(`${ApiService.AI_MODEL_REGISTRY_BASE}/defaults`, {
      method: 'PUT',
      body: JSON.stringify({ slot, model_id: modelId, reason: reason || null }),
    });
    return result.data;
  }

  async rollbackModel(slot: string) {
    const result = await this.fetchWithAuth(`${ApiService.AI_MODEL_REGISTRY_BASE}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ slot }),
    });
    return result.data;
  }

  async getModelHistory(slot?: string, limit = 20) {
    const qs = slot ? `?slot=${encodeURIComponent(slot)}&limit=${limit}` : `?limit=${limit}`;
    const result = await this.fetchWithAuth(`${ApiService.AI_MODEL_REGISTRY_BASE}/history${qs}`);
    return result.data;
  }

  // Triggers POST /refresh on admin-backend (rate-limited 1/30s per admin).
  async updateModelPricing(modelId: string, pricingUnit: string, pricingAmount: number): Promise<any> {
    const response = await fetch(`${this.baseUrl}${ApiService.AI_MODEL_REGISTRY_BASE}/pricing`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model_id: modelId, pricing_unit: pricingUnit, pricing_amount: pricingAmount }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err.message || `HTTP ${response.status}`);
    }
    return response.json();
  }

  // Returns the fresh catalog in the same shape as getAvailableModels.
  // Bypasses fetchWithAuth so { data, slots, last_synced_at, sync_age_hours, stale } survives.
  async refreshModels() {
    const response = await fetch(`${this.baseUrl}${ApiService.AI_MODEL_REGISTRY_BASE}/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      // Rate-limit → surface a 429-shaped error so useModelRegistry shows the "wait 30s" toast.
      const code = err.code || err.error_code || '';
      throw new Error(code === 'RATE_LIMITED' || response.status === 429
        ? `429 RATE_LIMITED ${err.message || ''}`
        : err.message || `HTTP ${response.status}`);
    }
    return await response.json();
  }

  /**
   * AI Moods Management APIs
   */
  async getAIMoods() {
    const result = await this.fetchWithAuth('/api/admin/moods');
    return result.data;
  }

  async getAIMoodsAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/moods/analytics');
    return result.data;
  }

  async createAIMood(moodData: any) {
    const result = await this.fetchWithAuth('/api/admin/moods', {
      method: 'POST',
      body: JSON.stringify(moodData),
    });
    return result.data;
  }

  async updateAIMood(moodId: number, moodData: any) {
    const result = await this.fetchWithAuth(`/api/admin/moods/${moodId}`, {
      method: 'PUT',
      body: JSON.stringify(moodData),
    });
    return result.data;
  }

  async deleteAIMood(moodId: number) {
    const result = await this.fetchWithAuth(`/api/admin/moods/${moodId}`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async toggleAIMood(id: number, isActive: boolean) {
    const result = await this.patch(`/api/admin/moods/${id}/toggle`, { isActive });
    return result.data;
  }

  async reorderAIMoods(items: Array<{ id: number; sort_order: number }>) {
    const result = await this.fetchWithAuth('/api/admin/moods/reorder', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
    return result.data;
  }

  /**
   * AI Styles Export APIs
   */
  async exportAIWritingStyles(): Promise<Blob> {
    try {
      const response = await fetch(`${this.baseUrl}/api/admin/ai-styles/writing-styles/export`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/pdf',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          console.log('🔐 Export Writing Styles: Auth error detected, but not redirecting to prevent race condition');
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to export writing styles: ${response.status} ${response.statusText}`);
      }
      
      return response.blob();
    } catch (error) {
      console.error('Export writing styles error:', error);
      throw error;
    }
  }

  async exportAIIllustrationStyles(): Promise<Blob> {
    try {
      const response = await fetch(`${this.baseUrl}/api/admin/ai-styles/illustration-styles/export`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/pdf',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          console.log('🔐 Export Illustration Styles: Auth error detected, but not redirecting to prevent race condition');
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to export illustration styles: ${response.status} ${response.statusText}`);
      }
      
      return response.blob();
    } catch (error) {
      console.error('Export illustration styles error:', error);
      throw error;
    }
  }

  async exportAIMoods(): Promise<Blob> {
    try {
      const response = await fetch(`${this.baseUrl}/api/admin/moods/export`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/pdf',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          console.log('🔐 Export Moods: Auth error detected, but not redirecting to prevent race condition');
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to export moods: ${response.status} ${response.statusText}`);
      }
      
      return response.blob();
    } catch (error) {
      console.error('Export moods error:', error);
      throw error;
    }
  }

  /**
   * Chapters Management APIs
   */

  /**
   * AI Queue Monitor APIs
   */
  async getAIQueueTasks() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/queue/tasks');
    return result.data;
  }

  // @deprecated Use getIntegratedAIAWSDashboard() instead
  async getAIQueueStats() {
    console.warn('⚠️ getAIQueueStats is deprecated. Use getIntegratedAIAWSDashboard() instead.');
    const result = await this.fetchWithAuth('/api/admin/cms/ai-queue-monitor');
    return result.data;
  }

  async getAIQueueChart() {
    const result = await this.fetchWithAuth('/api/admin/ai-styles/queue/chart');
    return result.data;
  }

  async cancelAITask(taskId: string) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/queue/tasks/${taskId}/cancel`, {
      method: 'POST',
    });
    return result.data;
  }

  async retryAITask(taskId: string) {
    const result = await this.fetchWithAuth(`/api/admin/ai-styles/queue/tasks/${taskId}/retry`, {
      method: 'POST',
    });
    return result.data;
  }

  /**
   * Integrated AI & AWS Monitor APIs
   */
  async getIntegratedAIAWSDashboard(timeRange: string = '24h') {
    const result = await this.fetchWithAuth(`/api/admin/integrated-ai-aws-monitor/dashboard?timeRange=${timeRange}`);
    return result.data;
  }

  async getIntegratedAIAWSMetricCards(timeRange: string = '24h') {
    const result = await this.fetchWithAuth(`/api/admin/integrated-ai-aws-monitor/metric-cards?timeRange=${timeRange}`);
    return result.data;
  }

  async getIntegratedAIAWSCostBreakdown(timeRange: string = '24h', groupBy: string = 'service') {
    const result = await this.fetchWithAuth(`/api/admin/integrated-ai-aws-monitor/cost-breakdown?timeRange=${timeRange}&groupBy=${groupBy}`);
    return result.data;
  }

  /**
   * Enhanced Revenue Analytics APIs
   */
  async getEnhancedRevenueAnalytics() {
    const result = await this.fetchWithAuth('/api/dashboard/overview');
    return result.data;
  }

  /**
   * Gross Margin Analytics APIs
   * V5.4.42: Extract grossMargins from /api/dashboard/financial response
   */
  async getGrossMarginAnalytics() {
    const result = await this.fetchWithAuth<Record<string, unknown>>('/api/dashboard/financial');
    const d = result.data;
    // Return the grossMargins object directly for the Overview dashboard
    return (d && typeof d === 'object' && 'grossMargins' in d ? (d as { grossMargins?: unknown }).grossMargins : undefined) || d;
  }

  /**
   * Grok Chapter Cost Analytics API
   * Pulls real data from grok_chapter_transformations and grok_text_enhancements tables
   */
  async getGrokChapterCosts(timeRange: string = 'today', startDate?: string, endDate?: string) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const result = await this.fetchWithAuth(`/api/admin/cost-analytics/grok-chapter?${params.toString()}`);
    return result.data;
  }

  /**
   * Grok Quest Cost Analytics API
   * Pulls real data from grok_quest_text_enhancements and quest_images tables
   */
  async getGrokQuestCosts(timeRange: string = 'today', startDate?: string, endDate?: string) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const result = await this.fetchWithAuth(`/api/admin/cost-analytics/grok-quest?${params.toString()}`);
    return result.data;
  }

  /**
   * Grok Summary Analytics API
   * Combined chapter/quest breakdown with totals
   */
  async getGrokSummary(timeRange: string = 'today', startDate?: string, endDate?: string) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const result = await this.fetchWithAuth(`/api/admin/cost-analytics/grok-summary?${params.toString()}`);
    return result.data;
  }

  /**
   * AWS Cost Analytics API
   */
  async getAWSCostAnalytics() {
    type AwsCostsPayload = {
      daily?: number | string;
      mtd?: number | string;
      last_month?: number | string;
      ytd?: number | string;
    };
    const result = await this.fetchWithAuth<AwsCostsPayload>('/api/admin/cost-analytics/aws-costs');
    const data = result.data;

    // Transform backend response to MetricCardData format for frontend
    const formatCurrency = (value: number | string) => {
      const num = typeof value === 'string' ? parseFloat(value) : value;
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const toNum = (v: number | string | undefined, fallback = 0): number => {
      if (v === undefined) return fallback;
      return typeof v === 'number' ? v : parseFloat(v);
    };

    return {
      dailyAWSCosts: {
        value: formatCurrency(data?.daily ?? 0),
        rawValue: toNum(data?.daily),
        label: 'Daily AWS Costs',
        subtitle: 'AWS costs today'
      },
      mtdAWSCosts: {
        value: formatCurrency(data?.mtd ?? 0),
        rawValue: toNum(data?.mtd),
        label: 'MTD AWS Costs',
        subtitle: 'Month to date AWS costs'
      },
      lastMonthAWSCosts: {
        value: formatCurrency(data?.last_month ?? 0),
        rawValue: toNum(data?.last_month),
        label: 'Last Month AWS Costs',
        subtitle: 'Previous month AWS costs'
      },
      ytdAWSCosts: {
        value: formatCurrency(data?.ytd ?? 0),
        rawValue: toNum(data?.ytd),
        label: 'YTD AWS Costs',
        subtitle: 'Year to date AWS costs'
      }
    };
  }

  /**
   * Voice Transcription Cost API (lightweight)
   */
  async getVoiceTranscriptionCosts(timeRange: string = 'today', startDate?: string, endDate?: string) {
    const params = new URLSearchParams({ timeRange });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const result = await this.fetchWithAuth(`/api/admin/cost-analytics/voice-transcription?${params.toString()}`);
    return result.data;
  }

  /**
   * User Management APIs
   */

  // Admin Users Management
  async getAdminUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) query.append(key, value.toString());
      });
    }
    const result = await this.fetchWithAuth(`/api/admin/user-management/admin-users?${query.toString()}`);
    return result.data;
  }

  async getAdminUser(id: number) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/admin-users/${id}`);
    return result.data;
  }

  async updateAdminUser(id: number, userData: {
    name?: string;
    role?: string;
    is_active?: boolean;
  }) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/admin-users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return result.data;
  }

  async revokeAdminSessions(id: number) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/admin-users/${id}/sessions`, {
      method: 'DELETE',
    });
    return result.data;
  }

  async getAdminUserStats() {
    const result = await this.fetchWithAuth('/api/admin/user-management/admin-users/stats');
    return result.data;
  }

  // Platform Users Management
  async getPlatformUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    subscription?: string;
    subscription_plan?: string;
    chapter_credits?: string | number;
    styles_purchased?: string | number;
    age_range?: string;
    family_vault_tier?: 'none' | 'standard' | 'premium' | 'all';
    /** Comma-separated ISO alpha-2 codes, or 'UNKNOWN', or 'all'. Multi-select. */
    location_country?: string;
    /** 'high' | 'medium' | 'low' | 'none' | 'all' */
    location_confidence?: string;
    /**
     * 'member' | 'flagged_test_candidate' | 'confirmed_test' | 'quarantined_test' | 'all'.
     * Defaults to 'member' server-side — test candidates are hidden unless asked for.
     */
    account_class?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') query.append(key, value.toString());
      });
    }
    const result = await this.fetchWithAuth(`/api/admin/user-management/platform-users?${query.toString()}`);
    return result.data;
  }

  /** Test-account review queue. `status` selects flagged candidates or quarantined accounts. */
  async getTestAccountQueue(status: 'flagged' | 'quarantined' = 'flagged'): Promise<{
    candidates: any[];
    bulkConfirmMinScore: number;
    playReconciliationAvailable: boolean;
  }> {
    const result = await this.fetchWithAuth(
      `/api/admin/user-management/test-accounts/queue?status=${status}`);
    return result.data;
  }

  /**
   * Act on one candidate. `confirm` quarantines (reversible, deletes nothing),
   * `reject` clears the flag permanently, `restore` undoes a quarantine.
   */
  async actOnTestAccount(id: string, action: 'confirm' | 'reject' | 'restore', reason?: string) {
    const result = await this.fetchWithAuth(
      `/api/admin/user-management/test-accounts/${id}/${action}`,
      { method: 'POST', body: JSON.stringify({ reason }) });
    return result.data;
  }

  /** Bulk quarantine. The server refuses anything below the allowlist-definitive threshold. */
  async bulkConfirmTestAccounts(ids: string[], reason?: string): Promise<{
    confirmed: Array<{ id: string; email: string }>;
    refused: Array<{ id: string; reason: string }>;
  }> {
    const result = await this.fetchWithAuth(
      '/api/admin/user-management/test-accounts/bulk-confirm',
      { method: 'POST', body: JSON.stringify({ ids, reason }) });
    return result.data;
  }

  /** Countries present in the member base, with counts, for the location filter. */
  async getPlatformMemberLocations(): Promise<{
    countries: Array<{
      country_code: string;
      member_count: number;
      high_confidence: number;
      medium_confidence: number;
      low_confidence: number;
    }>;
  }> {
    const result = await this.fetchWithAuth('/api/admin/user-management/platform-users/locations');
    return result.data;
  }

  async getPlatformUser(id: number) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/platform-users/${id}`);
    return result.data;
  }

  async updatePlatformUser(id: number, userData: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
    location?: string;
    bio?: string;
    age_range?: string;
    subscription_plan?: string;
    subscription_status?: string;
    chapter_credits?: number;
    has_unlimited_credits?: boolean;
    trial_chapters_remaining?: number;
    is_active?: boolean;
    vault_subscription_tier?: string;
    vault_subscription_status?: string;
  }) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/platform-users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return result.data;
  }

  async getPlatformUserStats() {
    const result = await this.fetchWithAuth('/api/admin/user-management/platform-users/stats');
    return result.data;
  }

  // Utility methods for backward compatibility
  async resetPlatformUserPassword(userId: number) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/platform-users/${userId}/reset-password`, {
      method: 'POST',
    });
    return result.data;
  }

  async bulkUpdatePlatformUsers(userIds: number[], action: string) {
    const result = await this.fetchWithAuth('/api/admin/user-management/platform-users/bulk', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds, action }),
    });
    return result.data;
  }

  async exportPlatformUsers(format: 'csv' | 'excel', filters?: any) {
    const result = await this.fetchWithAuth<{ download_url?: string }>('/api/admin/user-management/platform-users/export', {
      method: 'POST',
      body: JSON.stringify({ format, filters }),
    });

    // Handle file download
    if (result.data?.download_url) {
      window.open(result.data.download_url, '_blank');
    }

    return result.data;
  }

  // Subscription Management APIs
  async activateUserSubscription(userId: number, subscriptionData: {
    plan_type: string;
  }) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/platform-users/${userId}/subscription/activate`, {
      method: 'POST',
      body: JSON.stringify(subscriptionData),
    });
    return result.data;
  }

  async updateUserCredits(userId: number, creditData: {
    credits: number;
    operation?: 'set' | 'add' | 'subtract';
  }) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/platform-users/${userId}/credits/update`, {
      method: 'POST',
      body: JSON.stringify(creditData),
    });
    return result.data;
  }

  async cancelUserSubscription(userId: number, cancellationData: {
    reason?: string;
    immediate?: boolean;
  }) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/platform-users/${userId}/subscription/cancel`, {
      method: 'POST',
      body: JSON.stringify(cancellationData),
    });
    return result.data;
  }

  // =====================================================================
  // V4.3 Admin Dashboard User Management — create / plan / vault / verify
  // =====================================================================

  async createPlatformMember(payload: {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    subscription_plan: string;
    family_vault_tier?: 'standard' | 'premium';
    family_vault_cycle?: 'monthly' | 'annual';
    chapter_credits?: number;
    auto_verify_email?: boolean;
    send_welcome_email?: boolean;
    reason: string;
  }) {
    const result = await this.fetchWithAuth('/api/admin/user-management/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return result.data;
  }

  async updateUserSubscriptionPlan(userId: string, payload: {
    new_tier: string;
    reason: string;
    comp_grant?: boolean;
  }) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/users/${userId}/tier`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return result.data;
  }

  async grantFamilyVault(userId: string, payload: {
    tier: 'standard' | 'premium';
    cycle: 'monthly' | 'annual';
    reason: string;
  }) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/users/${userId}/family-vault`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return result.data;
  }

  async revokeFamilyVault(userId: string, reason: string) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/users/${userId}/family-vault`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
    return result.data;
  }

  async resendVerificationEmail(userId: string) {
    const result = await this.fetchWithAuth(`/api/admin/user-management/users/${userId}/resend-verification`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return result.data;
  }

  async getSubscriptionPlans(includeInactive = false) {
    const qs = includeInactive ? '?include_inactive=true' : '';
    const result = await this.fetchWithAuth(`/api/admin/user-management/subscription-plans${qs}`);
    return result.data;
  }

  /**
   * Member Management Analytics APIs
   */
  async getPlatformMemberStats() {
    const result = await this.fetchWithAuth('/api/admin/user-management/platform-stats');
    return result.data;
  }

  /** Onboarding age range x motivation cross-tab, filtered on member join date. */
  async getOnboardingBreakdown(range: { from: Date; to: Date }) {
    const params = new URLSearchParams({
      from: range.from.toISOString(),
      to: range.to.toISOString(),
    });
    const result = await this.fetchWithAuth<OnboardingBreakdownResponse>(
      `/api/admin/user-management/onboarding-breakdown?${params.toString()}`
    );
    return result.data;
  }

  /**
   * Signup date and first tracked app activity for a set of member emails.
   * first_app_activity_at is NOT an install date — see the route comment.
   */
  async getMemberSignupDates(emails: string[]) {
    const result = await this.fetchWithAuth<MemberSignupDatesResponse>(
      '/api/admin/user-management/member-signup-dates',
      {
        method: 'POST',
        body: JSON.stringify({ emails }),
      }
    );
    return result.data;
  }

  async getNewsletterAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/newsletter/analytics');
    return result.data;
  }

  async getAdminUserAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/user-management/admin-stats');
    return result.data;
  }

  async deleteAdminUser(userId: string): Promise<void> {
    await this.fetchWithAuth(`/api/admin/users/${userId}`, { method: 'DELETE' });
  }

  async getAdminActivityLog(page = 1, limit = 50): Promise<{ entries: any[]; total: number }> {
    const result = await this.fetchWithAuth(`/api/admin/users/activity-log?page=${page}&limit=${limit}`);
    return result.data as any;
  }

  /**
   * Free Tier Users Intelligence APIs
   */
  async getFreeTierSummary() {
    const result = await this.fetchWithAuth('/api/admin/free-tier/summary');
    return result.data;
  }

  async getFreeTierUsers(params: {
    page?: number; limit?: number; search?: string;
    engagement_tier?: string; recommended_action?: string;
    sort_by?: string; sort_dir?: string;
  } = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
    const result = await this.fetchWithAuth(`/api/admin/free-tier/users?${qs.toString()}`);
    return result.data;
  }

  async getFreeTierFunnel() {
    const result = await this.fetchWithAuth('/api/admin/free-tier/conversion-funnel');
    return result.data;
  }

  async getFreeTierCohorts() {
    const result = await this.fetchWithAuth('/api/admin/free-tier/cohort-timeline');
    return result.data;
  }

  async getFreeTierSegments() {
    const result = await this.fetchWithAuth('/api/admin/free-tier/audience-segments');
    return result.data;
  }

  async exportFreeTierCSV(params?: { engagement_tier?: string; recommended_action?: string }) {
    const qs = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
    return this.downloadBlob(`/api/admin/free-tier/export-csv?${qs.toString()}`, 'GET');
  }

  async sendFreeTierPromotion(payload: { segment: string; subject: string; message: string; coupon_code?: string }) {
    const result = await this.fetchWithAuth('/api/admin/free-tier/send-promotion', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return result.data;
  }

  // [CONV-ENGINE] ──────────────────────────────────────────────────────────────

  // Activation pipeline ribbon + KPI band (activation funnel only; no Singular).
  async getActivationPipeline() {
    return this.fetchWithAuth('/api/admin/free-tier/activation-pipeline');
  }

  // Members by Activation Position (paginated table).
  async getActivationPositions(params: {
    page?: number; limit?: number; phase?: string; funnel?: string;
    tier?: string; idle_min?: number; search?: string;
  } = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
    return this.fetchWithAuth(`/api/admin/free-tier/activation-positions?${qs.toString()}`);
  }

  // Nurture funnel controls (email-funnels service).
  // NOTE: getEmailFunnelsOverview is defined ONCE further below (in the
  // pre-existing "Email Funnels" block) and returns the unwrapped
  // { success, summary, flows }. Do not re-add it here — a duplicate method
  // would win and change the return shape, breaking the controls panel.

  async toggleEmailFunnel(flowId: string, status: 'active' | 'paused') {
    return this.fetchWithAuth(`/api/admin/email-funnels/${flowId}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async updateFunnelSteps(flowId: string, steps: Array<{ id: string; delay_hours?: number; include_offer?: boolean; step_order?: number; label?: string; step_type?: string; template_key?: string }>) {
    return this.fetchWithAuth(`/api/admin/email-funnels/${flowId}/steps`, {
      method: 'PATCH',
      body: JSON.stringify({ steps }),
    });
  }

  async addFunnelStep(flowId: string, payload: { step_type: string; label?: string; delay_hours?: number; include_offer?: boolean; template_key?: string; create_template?: boolean; category?: string }) {
    return this.fetchWithAuth(`/api/admin/email-funnels/${flowId}/steps`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteFunnelStep(flowId: string, stepId: string) {
    return this.fetchWithAuth(`/api/admin/email-funnels/${flowId}/steps/${stepId}`, {
      method: 'DELETE',
    });
  }

  async duplicateFunnelStep(flowId: string, stepId: string) {
    return this.fetchWithAuth(`/api/admin/email-funnels/${flowId}/steps/${stepId}/duplicate`, {
      method: 'POST',
    });
  }

  async getFunnelTemplates() {
    return this.fetchWithAuth('/api/admin/email-templates?limit=200');
  }

  async updateFunnelPromo(flowId: string, couponCode: string | null) {
    return this.fetchWithAuth(`/api/admin/email-funnels/${flowId}/promo`, {
      method: 'PATCH',
      body: JSON.stringify({ coupon_code: couponCode }),
    });
  }

  async getConversionEngineCohorts() {
    return this.fetchWithAuth('/api/admin/free-tier/cohorts');
  }

  async getCohortMembers(cohortName: string, page = 1, limit = 25) {
    return this.fetchWithAuth(
      `/api/admin/free-tier/cohorts/${encodeURIComponent(cohortName)}/members?page=${page}&limit=${limit}`,
    );
  }

  async getCohortVelocity(cohortName: string) {
    return this.fetchWithAuth(
      `/api/admin/free-tier/cohorts/${encodeURIComponent(cohortName)}/velocity`,
    );
  }

  async getUserEventTimeline(userId: string, limit = 50) {
    return this.fetchWithAuth(
      `/api/admin/free-tier/users/${userId}/event-timeline?limit=${limit}`,
    );
  }

  async getUserScoreHistory(userId: string, days = 90) {
    return this.fetchWithAuth(
      `/api/admin/free-tier/users/${userId}/score-history?days=${days}`,
    );
  }

  async getScoreDistribution() {
    return this.fetchWithAuth('/api/admin/free-tier/score-distribution');
  }

  async getCartRecoveryActive() {
    return this.fetchWithAuth('/api/admin/free-tier/cart-recovery/active');
  }

  async getCartRecoveryStats() {
    return this.fetchWithAuth('/api/admin/free-tier/cart-recovery/stats');
  }

  async getTrialFunnel(days = 30) {
    return this.fetchWithAuth(`/api/admin/free-tier/trial-funnel?days=${days}`);
  }

  async getFeatureAdoption() {
    return this.fetchWithAuth('/api/admin/free-tier/feature-adoption');
  }

  async getSubscriptionHealth(days = 30) {
    return this.fetchWithAuth(`/api/admin/free-tier/subscription-health?days=${days}`);
  }

  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Communications Overview API
   * Fetches aggregated data for all communication channels
   */
  async getCommunicationsOverview() {
    try {
      const result = await this.fetchWithAuth('/api/admin/communications/overview');
      return result.data;
    } catch (error) {
      console.warn('Communications overview endpoint not available, using fallback');
      // Try legacy endpoint as fallback
      try {
        const legacy = await this.fetchWithAuth('/api/admin/cms/communications/overview');
        return legacy.data;
      } catch {
        return {
          channels: {
            push: { sent: 0, status: 'Live' },
            sms: { sent: 0, status: 'Live' },
            email: { sent: 0, status: 'Live' },
            triggers: { active: 0, status: 'Live' }
          },
          health: {
            delivery_rate: null,
            open_rate: null,
            click_rate: null,
            bounce_rate: null
          },
          volume: {
            labels: [],
            push: [],
            sms: [],
            email: []
          },
          topTemplates: [],
          recentActivity: []
        };
      }
    }
  }

  /**
   * Email Analytics Overview API
   * Fetches detailed email system analytics for Email Hub
   */
  async getEmailAnalyticsOverview(period: string = '30d') {
    try {
      const result = await this.fetchWithAuth(`/api/admin/email-analytics/overview?period=${period}`);
      return result.data;
    } catch (error) {
      console.warn('Email analytics endpoint not available, using fallback');
      return {
        summary: { total_templates: 0, total_sent: 0, delivery_rate: null, avg_open_rate: null, active_triggers: 0, brevo_synced: 0 },
        volumeByDay: { labels: [], datasets: {} },
        categoryBreakdown: [],
        funnel: { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0 },
        templatePerformance: [],
        triggerHealth: [],
        brevoSync: { synced: 0, local_only: 0, pending: 0, failed: 0 },
        dayOfWeek: { labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], data: [0, 0, 0, 0, 0, 0, 0] }
      };
    }
  }
  // ============================================================================
  // Customer Support
  // ============================================================================

  async getCustomerSupportInbox(filter = 'all', search = '', accountId?: string, folder = 'inbox') {
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('filter', filter);
    if (search) params.set('search', search);
    if (accountId) params.set('accountId', accountId);
    if (folder !== 'inbox') params.set('folder', folder);
    const qs = params.toString();
    const result = await this.fetchWithAuth(`/api/admin/customer-support/inbox${qs ? '?' + qs : ''}`);
    return result;
  }

  async getCustomerSupportStats() {
    const result = await this.fetchWithAuth('/api/admin/customer-support/stats');
    return result;
  }

  async markSupportRead(externalId: string) {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/mark-read/${encodeURIComponent(externalId)}`, {
      method: 'POST',
    });
    return result;
  }

  async resolveSupportItem(externalId: string, data: { notes?: string; tags?: string[] }) {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/resolve/${encodeURIComponent(externalId)}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result;
  }

  async unresolveSupportItem(externalId: string) {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/unresolve/${encodeURIComponent(externalId)}`, {
      method: 'POST',
    });
    return result;
  }

  async sendSupportReply(data: { externalId: string; to: string; subject: string; body: string; threadId?: string; accountId?: string }) {
    const result = await this.fetchWithAuth('/api/admin/customer-support/reply-email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result;
  }

  async initiateSupportCallback(data: { phoneNumber: string; externalId?: string }) {
    const result = await this.fetchWithAuth('/api/admin/customer-support/callback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result;
  }

  // ============================================================================
  // Customer Support — Email Settings (Multi-Account)
  // ============================================================================

  /** Returns array of all active email accounts */
  async getEmailSettings() {
    const result = await this.fetchWithAuth('/api/admin/customer-support/email-settings');
    return result;
  }

  async saveEmailSettings(data: {
    email: string;
    password: string;
    displayName?: string;
    accountName?: string;
    provider?: string;
    color?: string;
    sortOrder?: number;
    imapHost: string;
    imapPort: number;
    imapSecure: boolean;
    smtpHost: string;
    smtpPort: number;
    smtpSecure: boolean;
  }) {
    const result = await this.fetchWithAuth('/api/admin/customer-support/email-settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result;
  }

  async updateEmailAccount(accountId: string, data: Record<string, any>) {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/email-settings/${encodeURIComponent(accountId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result;
  }

  async testEmailConnection(data: {
    email: string;
    password: string;
    imapHost: string;
    imapPort: number;
    imapSecure: boolean;
    smtpHost: string;
    smtpPort: number;
    smtpSecure: boolean;
  }) {
    const result = await this.fetchWithAuth('/api/admin/customer-support/test-connection', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result;
  }

  async deleteEmailSettings(accountId: string) {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/email-settings/${encodeURIComponent(accountId)}`, {
      method: 'DELETE',
    });
    return result;
  }

  async detectEmailProvider(email: string) {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/provider-settings/${encodeURIComponent(email)}`);
    return result;
  }

  // ============================================================================
  // Customer Support — Phone Numbers
  // ============================================================================

  async getPhoneNumbers() {
    const result = await this.fetchWithAuth('/api/admin/customer-support/phone-numbers');
    return result;
  }

  async createPhoneNumber(data: { label: string; phoneNumber: string; type?: string; notes?: string }) {
    const result = await this.fetchWithAuth('/api/admin/customer-support/phone-numbers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result;
  }

  async updatePhoneNumber(id: string, data: Record<string, any>) {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/phone-numbers/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result;
  }

  async deletePhoneNumber(id: string) {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/phone-numbers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    return result;
  }

  // ============================================================================
  // Customer Support — Compose (new email to any address)
  // ============================================================================

  async sendComposeEmail(data: {
    accountId?: string;
    to: string;
    cc?: string;
    subject: string;
    bodyHtml: string;
    bodyText?: string;
  }): Promise<{ success: boolean; data?: { messageId: string } }> {
    const result = await this.fetchWithAuth('/api/admin/customer-support/send-email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result as { success: boolean; data?: { messageId: string } };
  }

  // ============================================================================
  // Customer Support — Tags
  // ============================================================================

  async getSupportTags(): Promise<{ id: string; name: string; color: string; created_at: string }[]> {
    const result = await this.fetchWithAuth('/api/admin/customer-support/tags') as any;
    return result?.data ?? [];
  }

  async createSupportTag(data: { name: string; color?: string }): Promise<{ id: string; name: string; color: string }> {
    const result = await this.fetchWithAuth('/api/admin/customer-support/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    }) as any;
    return result?.data;
  }

  async updateInteractionTags(externalId: string, tags: string[]): Promise<void> {
    await this.fetchWithAuth(`/api/admin/customer-support/interaction/${encodeURIComponent(externalId)}/tags`, {
      method: 'PATCH',
      body: JSON.stringify({ tags }),
    });
  }

  // ============================================================================
  // Customer Support — Drafts
  // ============================================================================

  async getSupportDrafts(): Promise<any[]> {
    const result = await this.fetchWithAuth('/api/admin/customer-support/drafts') as any;
    return result?.data ?? [];
  }

  async createSupportDraft(data: {
    emailAccountId?: string;
    toAddress: string;
    cc?: string;
    subject?: string;
    bodyHtml?: string;
    bodyText?: string;
    inReplyToExternalId?: string;
  }): Promise<any> {
    const result = await this.fetchWithAuth('/api/admin/customer-support/drafts', {
      method: 'POST',
      body: JSON.stringify(data),
    }) as any;
    return result?.data;
  }

  async updateSupportDraft(id: string, data: {
    emailAccountId?: string;
    toAddress?: string;
    cc?: string;
    subject?: string;
    bodyHtml?: string;
    bodyText?: string;
  }): Promise<any> {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/drafts/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }) as any;
    return result?.data;
  }

  async deleteSupportDraft(id: string): Promise<void> {
    await this.fetchWithAuth(`/api/admin/customer-support/drafts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async sendSupportDraft(id: string): Promise<{ messageId: string }> {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/drafts/${encodeURIComponent(id)}/send`, {
      method: 'POST',
    }) as any;
    return result?.data;
  }

  // ============================================================================
  // Customer Support — Funnels
  // ============================================================================

  async getSupportFunnels(): Promise<any[]> {
    const result = await this.fetchWithAuth('/api/admin/customer-support/funnels') as any;
    return result?.data ?? [];
  }

  async createSupportFunnel(data: {
    name: string;
    description?: string;
    color?: string;
    stages?: { name: string; stageOrder?: number; color?: string }[];
  }): Promise<any> {
    const result = await this.fetchWithAuth('/api/admin/customer-support/funnels', {
      method: 'POST',
      body: JSON.stringify(data),
    }) as any;
    return result?.data;
  }

  async updateSupportFunnel(id: string, data: {
    name?: string;
    description?: string;
    color?: string;
    isActive?: boolean;
    sortOrder?: number;
    stages?: { name: string; stageOrder?: number; color?: string }[];
  }): Promise<any> {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/funnels/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }) as any;
    return result?.data;
  }

  async deleteSupportFunnel(id: string): Promise<void> {
    await this.fetchWithAuth(`/api/admin/customer-support/funnels/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async getSupportFunnelBoard(funnelId: string): Promise<any> {
    const result = await this.fetchWithAuth(`/api/admin/customer-support/funnels/${encodeURIComponent(funnelId)}/board`) as any;
    return result?.data;
  }

  async assignInteractionToFunnel(externalId: string, data: { funnelId: string; stageId: string }): Promise<void> {
    await this.fetchWithAuth(`/api/admin/customer-support/interaction/${encodeURIComponent(externalId)}/funnel`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeInteractionFromFunnel(externalId: string, funnelId: string): Promise<void> {
    await this.fetchWithAuth(`/api/admin/customer-support/interaction/${encodeURIComponent(externalId)}/funnel/${encodeURIComponent(funnelId)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Onboarding V2 Segments API
   */
  async getOnboardingSegmentsSummary() {
    const result = await this.fetchWithAuth('/api/admin/onboarding-segments/summary');
    return result.data;
  }

  async getOnboardingSegmentUsers(params?: {
    identity?: string;
    motivation?: string;
    output_preference?: string;
    age_range?: string;
    limit?: number;
    offset?: number;
  }) {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) qs.set(k, String(v));
      });
    }
    const result = await this.fetchWithAuth(`/api/admin/onboarding-segments/users?${qs.toString()}`);
    return result.data;
  }

  // ============================================================================
  // Grok Imagine Video — Prompt variation management
  // ============================================================================

  async getGrokImagineVariations() {
    const result = await this.fetchWithAuth('/api/admin/grok-imagine/variations');
    return result.data;
  }

  async getGrokImagineAnalytics() {
    const result = await this.fetchWithAuth('/api/admin/grok-imagine/analytics');
    return result.data;
  }

  async getGrokImagineSceneLog(params: { page: number; limit: number; variation_type?: string }) {
    const query = new URLSearchParams({
      page: params.page.toString(),
      limit: params.limit.toString(),
      ...(params.variation_type && { variation_type: params.variation_type }),
    }).toString();
    const result = await this.fetchWithAuth(`/api/admin/grok-imagine/scene-log?${query}`);
    return result.data;
  }

  async activateGrokImagineVariation(variationType: string) {
    const result = await this.fetchWithAuth(
      `/api/admin/grok-imagine/variations/${encodeURIComponent(variationType)}/activate`,
      { method: 'POST' }
    );
    return result.data;
  }

  async updateGrokImagineVariation(variationType: string, data: Record<string, any>) {
    const result = await this.fetchWithAuth(
      `/api/admin/grok-imagine/variations/${encodeURIComponent(variationType)}`,
      { method: 'PUT', body: JSON.stringify(data) }
    );
    return result.data;
  }

  async resetGrokImagineDefaults() {
    const result = await this.fetchWithAuth('/api/admin/grok-imagine/variations/reset-defaults', {
      method: 'POST',
    });
    return result.data;
  }

  async testGrokImaginePrompt(params: { image_url: string; variation_type?: string }) {
    const result = await this.fetchWithAuth('/api/admin/grok-imagine/test', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return result.data;
  }

  // ============================================================
  // Phase B.4: Dynamic Segments
  // ============================================================
  async listDynamicSegments(params: { active?: boolean; search?: string } = {}) {
    const query = new URLSearchParams();
    if (typeof params.active === 'boolean') query.set('active', String(params.active));
    if (params.search) query.set('search', params.search);
    const qs = query.toString();
    return this.fetchWithAuth(`/api/admin/segments${qs ? `?${qs}` : ''}`);
  }

  async getDynamicSegmentSchema() {
    return this.fetchWithAuth('/api/admin/segments/schema');
  }

  async getDynamicSegment(id: string) {
    return this.fetchWithAuth(`/api/admin/segments/${id}`);
  }

  async createDynamicSegment(body: {
    name: string;
    description?: string;
    slug?: string;
    is_active?: boolean;
    filter_json: Record<string, unknown>;
  }) {
    return this.fetchWithAuth('/api/admin/segments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateDynamicSegment(id: string, body: {
    name?: string;
    description?: string | null;
    filter_json?: Record<string, unknown>;
    is_active?: boolean;
  }) {
    return this.fetchWithAuth(`/api/admin/segments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async deleteDynamicSegment(id: string) {
    return this.fetchWithAuth(`/api/admin/segments/${id}`, { method: 'DELETE' });
  }

  async previewDynamicSegment(filter_json: Record<string, unknown>, limit = 20) {
    return this.fetchWithAuth('/api/admin/segments/preview', {
      method: 'POST',
      body: JSON.stringify({ filter_json, limit }),
    });
  }

  async refreshDynamicSegment(id: string) {
    return this.fetchWithAuth(`/api/admin/segments/${id}/refresh`, { method: 'POST' });
  }

  async getDynamicSegmentMembers(id: string, limit = 100, offset = 0) {
    return this.fetchWithAuth(`/api/admin/segments/${id}/members?limit=${limit}&offset=${offset}`);
  }

  async getDynamicSegmentRefreshLog(id: string) {
    return this.fetchWithAuth(`/api/admin/segments/${id}/refresh-log`);
  }

  // ── Phase B.3: Flow / journey engine ──────────────────────────────────────

  async listEmailFlows(params: { status?: string; search?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    const q = qs.toString();
    const result = await this.fetchWithAuth(`/api/admin/flows${q ? `?${q}` : ''}`);
    return result.data;
  }

  async getEmailFlow(flowId: string) {
    const result = await this.fetchWithAuth(`/api/admin/flows/${flowId}`);
    return result.data;
  }

  async createEmailFlow(body: {
    slug: string;
    name: string;
    description?: string;
    trigger_event: string;
    trigger_config?: Record<string, unknown>;
    steps: Array<{ step_order?: number; step_type: string; config: Record<string, unknown>; label?: string }>;
    status?: 'draft' | 'active' | 'paused' | 'archived';
    max_concurrent_enrollments_per_user?: number;
    exit_on_unsubscribe?: boolean;
  }) {
    const result = await this.fetchWithAuth('/api/admin/flows', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return result.data;
  }

  async updateEmailFlow(flowId: string, body: Partial<{
    name: string;
    description: string;
    trigger_event: string;
    trigger_config: Record<string, unknown>;
    steps: Array<{ step_order?: number; step_type: string; config: Record<string, unknown>; label?: string }>;
    status: 'draft' | 'active' | 'paused' | 'archived';
    max_concurrent_enrollments_per_user: number;
    exit_on_unsubscribe: boolean;
  }>) {
    const result = await this.fetchWithAuth(`/api/admin/flows/${flowId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return result.data;
  }

  async deleteEmailFlow(flowId: string) {
    const result = await this.fetchWithAuth(`/api/admin/flows/${flowId}`, { method: 'DELETE' });
    return result.data;
  }

  async enrollIntoFlow(flowId: string, body: { user_id?: string; subscriber_email?: string; metadata?: Record<string, unknown> }) {
    const result = await this.fetchWithAuth(`/api/admin/flows/${flowId}/enroll`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return result.data;
  }

  async listFlowEnrollments(flowId: string, params: { status?: string; limit?: number; offset?: number } = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString();
    const result = await this.fetchWithAuth(`/api/admin/flows/${flowId}/enrollments${q ? `?${q}` : ''}`);
    return result.data;
  }

  async getFlowEnrollmentTimeline(enrollmentId: string) {
    const result = await this.fetchWithAuth(`/api/admin/flows/enrollments/${enrollmentId}/timeline`);
    return result.data;
  }

  async exitFlowEnrollment(enrollmentId: string, reason?: string) {
    const result = await this.fetchWithAuth(`/api/admin/flows/enrollments/${enrollmentId}/exit`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    });
    return result.data;
  }

  // ── Email Funnels ─────────────────────────────────────────────────────────

  async getEmailFunnelsOverview(): Promise<EmailFunnelsOverview> {
    const result = await this.fetchWithAuth<EmailFunnelsOverview>('/api/admin/email-funnels/overview');
    return result.data;
  }

  async getFunnelEligibleMembers(flowId: string, limit = 500) {
    const result = await this.fetchWithAuth<{ members: Array<{
      user_id: string; name: string | null; email: string | null;
      hours_idle: number | null; engagement_tier: string | null; is_subscriber: boolean | null;
    }>; total: number }>(`/api/admin/email-funnels/${flowId}/eligible-members?limit=${limit}`);
    return result.data;
  }

  async getEmailFunnelAnalytics(flowId: string) {
    const result = await this.fetchWithAuth(`/api/admin/email-funnels/${flowId}/analytics`);
    return result.data;
  }

  async toggleEmailFunnelStatus(flowId: string, status: 'active' | 'paused') {
    const result = await this.fetchWithAuth(`/api/admin/email-funnels/${flowId}/toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    return result.data as { flow: any; stub_warning: string | null };
  }

  async getEmailFunnelTemplateHealth() {
    const result = await this.fetchWithAuth('/api/admin/email-funnels/template-health');
    return result.data;
  }

  // ── Audiences ─────────────────────────────────────────────────────────────

  async getAudiences() {
    const result = await this.fetchWithAuth('/api/admin/audiences');
    return result.data as { audiences: any[]; cohort_count: number; segment_count: number };
  }

  async getPrebuiltAudiences() {
    const result = await this.fetchWithAuth('/api/admin/audiences/prebuilt');
    return result.data as { audiences: any[] };
  }

  async getAudienceCount(audienceId: string) {
    const result = await this.fetchWithAuth(`/api/admin/audiences/${audienceId}/count`);
    return result.data as { count: number };
  }

  async createAudience(data: { name: string; description?: string; filter_json: Record<string, unknown>; color?: string }) {
    const result = await this.fetchWithAuth('/api/admin/audiences', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result.data as { audience: any };
  }

  async updateAudience(id: string, data: { name?: string; description?: string; filter_json?: Record<string, unknown>; color?: string }) {
    const result = await this.fetchWithAuth(`/api/admin/audiences/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return result.data as { audience: any };
  }

  // ── Survey tracking ───────────────────────────────────────────────────────
  // Backed by /api/admin/survey/*, which proxies the standalone survey Lambda
  // (DynamoDB, not Aurora). Note fetchWithAuth unwraps {success, data} to {data},
  // but the survey API returns its payload at the top level, so `data` here is
  // the whole body.

  /**
   * The campaign every survey call is scoped to. Undefined means the live one, which
   * is what the server defaults to — so a call that omits it behaves exactly as it
   * did before the switcher existed.
   */
  private surveyScope(campaign?: string, extra?: URLSearchParams) {
    const p = extra || new URLSearchParams();
    if (campaign) p.set('campaign', campaign);
    const qs = p.toString();
    return qs ? `?${qs}` : '';
  }

  async getSurveyCampaigns() {
    const result = await this.fetchWithAuth('/api/admin/survey/campaigns');
    return result.data as any;
  }

  /** Creates a campaign, normally by cloning one that already works. */
  async createSurveyCampaign(payload: {
    id: string;
    title?: string;
    from?: string;
    tag_prefix?: string;
    promo_code?: string;
  }) {
    const result = await this.fetchWithAuth('/api/admin/survey/campaigns', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return result.data as any;
  }

  async getSurveyFunnel(campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/funnel${this.surveyScope(campaign)}`);
    return result.data as any;
  }

  async getSurveyRecipients(filters?: { state?: string; search?: string; campaign?: string }) {
    const params = new URLSearchParams();
    if (filters?.state) params.append('state', filters.state);
    if (filters?.search) params.append('search', filters.search);
    const qs = this.surveyScope(filters?.campaign, params);
    const result = await this.fetchWithAuth(`/api/admin/survey/recipients${qs}`);
    return result.data as any;
  }

  async getSurveyResponses(filters?: { page?: number; limit?: number; search?: string; campaign?: string }) {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.search) params.append('search', filters.search);
    const qs = this.surveyScope(filters?.campaign, params);
    const result = await this.fetchWithAuth(`/api/admin/survey/responses${qs}`);
    return result.data as any;
  }

  async getSurveySummary(campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/summary${this.surveyScope(campaign)}`);
    return result.data as any;
  }

  /** Refreshes the Brevo event snapshot. Returns ok:false when a tag failed (207). */
  async syncSurveyTracking() {
    const result = await this.fetchWithAuth('/api/admin/survey/sync', { method: 'POST' });
    return result.data as any;
  }

  async exportSurveyResponses(campaign?: string) {
    const stamp = new Date().toISOString().split('T')[0];
    // downloadFile sends cookies and triggers the save dialog; a plain
    // window.location.href navigation would lose both the auth context and any
    // chance of surfacing an error.
    await this.downloadFile(`/api/admin/survey/export.csv${this.surveyScope(campaign)}`, `survey-responses-${stamp}.csv`);
    return { success: true };
  }

  async getSurveySettings(campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/settings${this.surveyScope(campaign)}`);
    return result.data as any;
  }

  /** Writes the follow-up schedule. Requires `full` on audience_manager. */
  async updateSurveySettings(settings: Record<string, unknown>, campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/settings${this.surveyScope(campaign)}`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
    return result.data as any;
  }

  /** Who WOULD get a follow-up right now — evaluated even while the feature is off. */
  async previewSurveyFollowups(campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/followups/preview${this.surveyScope(campaign)}`);
    return result.data as any;
  }

  /** Runs the follow-up pass immediately instead of waiting for the schedule. */
  async runSurveyFollowups() {
    const result = await this.fetchWithAuth('/api/admin/survey/followups/run', { method: 'POST' });
    return result.data as any;
  }

  /* ── Survey email copy ───────────────────────────────────────────────────── */
  // All four emails live as fields on one campaign definition in DynamoDB. `rev` is
  // the optimistic-lock token: whatever a read hands back must be sent with the next
  // write, or the save is refused rather than flattening someone else's edit.

  async getSurveyEmails(campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/emails${this.surveyScope(campaign)}`);
    return result.data as any;
  }

  /** Saves one email's copy. Requires `full` on audience_manager. 409 on a stale rev. */
  async updateSurveyEmail(payload: {
    id: string;
    values: Record<string, unknown>;
    expected_rev: number;
  }, campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/emails${this.surveyScope(campaign)}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return result.data as any;
  }

  /**
   * Renders one email exactly as a member would receive it. Pass `values` to preview
   * unsaved edits — without them the modal could only ever show what is already
   * stored, so an admin would have to save to see their own wording.
   */
  async renderSurveyEmail(payload: { id: string; values?: Record<string, unknown> }, campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/emails/render${this.surveyScope(campaign)}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return result.data as any;
  }

  /** Sends one email to one address. Defaults to the signed-in admin server-side. */
  async testSendSurveyEmail(payload: {
    id: string;
    to?: string;
    values?: Record<string, unknown>;
  }, campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/emails/testsend${this.surveyScope(campaign)}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return result.data as any;
  }

  /* ── Roster ──────────────────────────────────────────────────────────────── */

  /**
   * Pulls newly eligible members out of Aurora and adds them to the survey list.
   * This one runs SQL in admin-backend rather than proxying, so it can be slow;
   * it never sends email. Requires `full` on audience_manager.
   */
  async refreshSurveyRoster(days?: number, campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/roster/refresh${this.surveyScope(campaign)}`, {
      method: 'POST',
      body: JSON.stringify(days ? { days } : {}),
    });
    return result.data as any;
  }

  /** Who has a survey link but has never been sent one. Does not send. */
  async getPendingSurveyInvites(campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/invites/pending${this.surveyScope(campaign)}`);
    return result.data as any;
  }

  /** Sends the invite to everyone pending, up to `limit`. Requires `full`. */
  async sendSurveyInvites(limit?: number, campaign?: string) {
    const result = await this.fetchWithAuth(`/api/admin/survey/invites/run${this.surveyScope(campaign)}`, {
      method: 'POST',
      body: JSON.stringify(limit ? { limit } : {}),
    });
    return result.data as any;
  }

  async getSuppressedSurveyRecipients() {
    const result = await this.fetchWithAuth('/api/admin/survey/roster/suppressed');
    return result.data as any;
  }

  /**
   * Adds or removes one exclusion. Suppression is what makes the refresh button
   * safe — without it every refresh re-adds people who were deliberately removed.
   */
  async suppressSurveyRecipient(email: string, opts?: { reason?: string; remove?: boolean }) {
    const result = await this.fetchWithAuth('/api/admin/survey/roster/suppress', {
      method: 'POST',
      body: JSON.stringify({ email, reason: opts?.reason, remove: opts?.remove }),
    });
    return result.data as any;
  }
}

export const apiService = new ApiService();
export default apiService;